/**
 * Le notifiche che scrive **il sistema**, in tre lingue, senza passare da un traduttore.
 *
 * ## Il fatto, misurato
 *
 * Fino a oggi ogni notifica nasceva come una frase italiana e le altre due lingue le
 * faceva la macchina (K75, `notification_translations` + LibreTranslate in casa). Per una
 * **circolare** — testo libero che una persona ha scritto — quella è l'unica strada
 * possibile: nessuno può prevedere che cosa scriverà l'ufficio del personale.
 *
 * Per le notifiche che compone il sistema è invece la strada sbagliata, e non per gusto.
 * Provate su LibreTranslate della macchina di collaudo il 2026-09-05:
 *
 * ```
 * it  Da firmare: ANNICCHIARICO MARTINA, BETTI RAISSA e altre 2.
 * en  To sign:  .
 * zh  要签名 :.
 *
 * it  Riunione «Prove tessuto FW27» il 10/09 alle 14:30, sala Tintoria.
 * en  Meeting «Prove FW27 fabric» 10/09 at 14:30, Tintoria room.
 * zh  会议“证明FW27织物”.
 * ```
 *
 * Due guasti, e sono i due peggiori possibili per una notifica di lavoro. Il primo: i nomi
 * scritti in maiuscolo — cioè **come stanno in anagrafica**, «BALESTRI MARCO» — vengono
 * **cancellati**, e la frase arriva vuota con la punteggiatura al suo posto, che è la forma
 * di guasto che nessuno segnala perché sembra un errore di battitura. Il secondo: il cinese
 * ha buttato via **il giorno, l'ora e la sala**, cioè tutto quello per cui quella notifica
 * esiste, tenendo solo il titolo della riunione.
 *
 * Aggiungere contesto alle notifiche — che è quello che è stato chiesto — senza cambiare
 * questa strada vorrebbe dire che più informazioni si mettono, più se ne perdono, e solo
 * per chi legge in inglese o in cinese: cioè costruire di nuovo, con le mani, il difetto
 * per cui K75 è stato scritto.
 *
 * ## La strada
 *
 * Qui i fatti non si traducono: si **rendono**. Una notifica di sistema dichiara una
 * chiave e i suoi dati (`{ chiave: 'riunione.invito', parametri: { … } }`), e per ognuna
 * delle tre lingue c'è una frase scritta a mano che li mette in fila. Nessun traduttore
 * automatico tocca queste righe, quindi:
 *
 * - **un nome non si traduce e non si perde.** Passa dentro la frase come un dato, che è
 *   quello che è;
 * - **una data si scrive come si scrive in quella lingua**, con `Intl` e il fuso
 *   aziendale, invece di essere «10/09» dato in pasto a un motore che in cinese lo ha reso
 *   «09年10月10日»;
 * - **niente sparisce.** Se una frase è sbagliata è sbagliata in modo visibile, in questo
 *   file, e la si corregge una volta per tutte le notifiche di quella specie — invece che
 *   in un motore statistico che non si può correggere.
 *
 * ## Come si innesta su quello che c'era
 *
 * Non lo sostituisce: lo **precede**. `NotificationsService.notify` continua a scrivere il
 * titolo e il corpo italiani nella riga della notifica — la bacheca, i rapporti, il
 * registro e chi legge in italiano non vedono nessuna differenza — e in più riempie subito
 * le due righe di `notification_translations` con l'inglese e il cinese di qui, già
 * `PRONTA`. Da lì in poi tutto il resto è già scritto: `scegliTesto` le trova, la bacheca
 * e la coda della spinta le usano, e il giro automatico non le tocca perché pesca solo
 * quelle `IN_ATTESA`.
 *
 * `origin` resta `AUTOMATICA`, e la scelta va detta: quell'enum distingue «l'ha corretta
 * una persona» da «no», e queste non le ha corrette nessuno — sono rese da un modello. Fra
 * i due valori esistenti, `AUTOMATICA` sbaglia nella direzione prudente: la schermata
 * dichiara «traduzione automatica» e chi legge pesa la frase con la stessa cautela di
 * prima. Dichiararla `MANO` avrebbe detto che un traduttore l'ha rivista, che non è vero.
 * Un terzo valore — «resa da un modello» — sarebbe più preciso e vuole una migrazione
 * dell'enum in banca dati e una parola nuova in due interfacce: sta scritto qui perché sia
 * una scelta e non una svista.
 *
 * ## Le regole per chi aggiunge una voce
 *
 * 1. **Fatti, non giudizi.** «La richiesta è ferma da 3 giorni» è un fatto; «richiesta
 *    urgente» è un giudizio che il sistema non è in grado di dare. Vale anche per gli
 *    aggettivi che sembrano innocui: «piccolo ritardo», «tutto a posto».
 * 2. **Le cose che contano nei primi ~80 caratteri del corpo.** Oltre, il sistema tronca
 *    sulla schermata di blocco, e tronca senza dire quanto. Il titolo dice *che cosa*, la
 *    prima frase del corpo dice *quando e chi*, il resto è per chi apre.
 * 3. **Niente parole vuote.** «Aggiornamento», «Notifica», «Avviso»: un titolo che si
 *    potrebbe mettere su qualunque messaggio non aiuta nessuno a decidere se aprire.
 * 4. **Ogni parametro compare in tutte e tre le frasi.** Una lingua che perde un dato che
 *    le altre hanno è lo stesso difetto di prima, scritto a mano.
 */
import { ExpenseNoteTypeCode, JustificationTypeCode } from './enums.js';
import { LINGUE } from './lingua.js';
/**
 * Il codice di lingua di `Intl`, uno per ognuna delle nostre.
 *
 * `en-GB` e non `en-US`: le date qui si leggono giorno/mese, e un `10/09` reso «September
 * 10» a chi lavora in un magazzino in Toscana sarebbe una data che si legge al contrario
 * proprio nel formato in cui è ambigua.
 */
const LOCALE = {
    it: 'it-IT',
    en: 'en-GB',
    zh: 'zh-CN',
};
/**
 * Giorno e ora insieme: «lun 10 set, 14:30», «Mon 10 Sep, 14:30», «9月10日周一 14:30».
 *
 * Il giorno della settimana c'è di proposito. Una riunione «il 10/09» costringe chi legge
 * a cercare il calendario per sapere se è dopodomani; «lun 10 set» risponde da sé, ed è la
 * differenza fra una notifica che si legge e una che si deve verificare.
 */
export function quandoTesto(istante, fuso, lingua) {
    return new Intl.DateTimeFormat(LOCALE[lingua], {
        timeZone: fuso,
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
    }).format(istante);
}
/** Solo il giorno, per quel che dura giorni interi e non ha un'ora. */
export function giornoTesto(giorno, fuso, lingua) {
    return new Intl.DateTimeFormat(LOCALE[lingua], {
        timeZone: fuso,
        weekday: 'short',
        day: 'numeric',
        month: 'short',
    }).format(giorno);
}
/** Solo l'ora, per la seconda metà di un intervallo che comincia e finisce lo stesso giorno. */
export function oraTesto(istante, fuso, lingua) {
    return new Intl.DateTimeFormat(LOCALE[lingua], {
        timeZone: fuso,
        hour: '2-digit',
        minute: '2-digit',
    }).format(istante);
}
/**
 * «da 3 giorni», «da 27 ore» — quanto ha aspettato, in unità che si leggono.
 *
 * Le stesse soglie in tutte e tre le lingue, perché il fatto è lo stesso: sotto le 48 ore
 * si contano le ore (dire «da 1 giorno» a chi aspetta da 27 ore arrotonda per difetto
 * proprio dove la fretta comincia), poi i giorni, poi i mesi.
 */
export function daQuantoTesto(ore, lingua) {
    if (ore < 48) {
        const n = Math.round(ore);
        // Il singolare (unoAlSingolare): una soglia a un'ora si puo' impostare, e diceva «da 1 ore».
        if (n === 1)
            return lingua === 'it' ? 'un’ora' : lingua === 'en' ? 'an hour' : '1小时';
        return lingua === 'it' ? `${n} ore` : lingua === 'en' ? `${n} hours` : `${n}小时`;
    }
    const giorni = Math.round(ore / 24);
    if (giorni < 60) {
        return lingua === 'it' ? `${giorni} giorni` : lingua === 'en' ? `${giorni} days` : `${giorni}天`;
    }
    const mesi = Math.round(giorni / 30);
    return lingua === 'it' ? `${mesi} mesi` : lingua === 'en' ? `${mesi} months` : `${mesi}个月`;
}
/** Il testo di una notifica in una lingua: le due cose che il telefono mostra. */
/**
 * Quante righe porta una raccolta nel suo avviso espanso: **cinque**.
 *
 * Non e' un numero scelto: e' quello che Android disegna in un `InboxStyle`, e una sesta riga
 * non comparirebbe ne' avviserebbe nessuno. Tagliare dove si vede e' meglio che farsi tagliare
 * dove non si vede. Su iPhone il corpo lungo non ha questo limite, ma cinque righe e' anche il
 * punto oltre il quale una notifica smette di essere una notifica.
 */
const RIGHE_RACCOLTA = 5;
/**
 * «, sala Tintoria» / «, Tintoria room» / «，Tintoria», o niente quando la sala non c'è.
 *
 * La parola «sala» non si mette se il nome ce l'ha già: le sale di questa azienda si
 * chiamano «Sala Prato», e il prefisso automatico produceva «sala Sala Prato». Il
 * controllo è sul nome come lo ha scritto chi amministra — non si corregge il nome, si
 * evita di raddoppiarlo — ed è per questo che guarda l'inizio della stringa senza toccarla.
 */
function dove(sala, l, luogo) {
    // Un luogo personale si scrive come l'ha scritto chi l'ha salvato: «Ufficio di Matteo» non è
    // una sala, e «sala Ufficio di Matteo» sarebbe una sala che non esiste ([[K667]]).
    if (sala === null) {
        if (!luogo)
            return '';
        return l === 'zh' ? `，${luogo}` : `, ${luogo}`;
    }
    if (l === 'zh')
        return `，${sala}`;
    const gia = /^(sala|stanza|room)\b/i.test(sala.trim());
    if (gia)
        return `, ${sala}`;
    return l === 'it' ? `, sala ${sala}` : `, room ${sala}`;
}
/**
 * La prima lettera maiuscola, per quando un pezzo pensato per stare in mezzo a una frase si
 * trova a cominciarla.
 *
 * {@link periodo} nasce minuscolo perché il suo posto normale è dopo il tipo di assenza —
 * «ROL il ven 18 set» — ma nella proposta di ferie apre il corpo, e «dal lun 5 ott…» sotto
 * un titolo si legge come una frase troncata a metà. In cinese non ci sono maiuscole e
 * `toUpperCase` su un ideogramma non fa niente: la funzione è innocua lì, e non vale un
 * ramo per dirlo.
 */
function maiuscola(s) {
    return s.length === 0 ? s : s[0].toUpperCase() + s.slice(1);
}
/**
 * «Riallineamento dal gestionale: 12 giri falliti», nelle tre lingue.
 *
 * I tre modi restano distinti nella frase, e non per precisione: chi legge deve capire in due
 * secondi se andare a guardare l'errore (fallisce), a guardare se l'API è su (muta), o se
 * qualcuno ha aggiunto un lavoro e non l'ha mai registrato (mai partita). «È guasta» li
 * manderebbe tutt'e tre nello stesso posto, che è quello sbagliato per due.
 */
function guasto(g, l) {
    if (g.che === 'maiPartita') {
        const che = l === 'it' ? 'non è mai partita' : l === 'en' ? 'has never run' : '从未运行过';
        return l === 'zh' ? `${g.lavoro}：${che}` : `${g.lavoro}: ${che}`;
    }
    if (g.che === 'silenzio') {
        const da = daQuantoTesto(g.quanto ?? 0, l);
        const che = l === 'it' ? `ferma da ${da}` : l === 'en' ? `silent for ${da}` : `已停止${da}`;
        return l === 'zh' ? `${g.lavoro}：${che}` : `${g.lavoro}: ${che}`;
    }
    const n = g.quanto ?? 0;
    if (g.che === 'scarta') {
        /*
          «finisce bene» sta nella frase, e non è un ornamento: è la sola cosa che distingua questo
          avviso da quello dei giri falliti per chi lo legge sul telefono. Senza, chi riceve va a
          cercare un errore nel pannello, lo trova verde, e conclude che l'avviso ha sbagliato.
        */
        const che = l === 'it'
            ? `${n} ${n === 1 ? 'giro' : 'giri'} ${n === 1 ? 'finito' : 'finiti'} bene ma con righe lasciate indietro`
            : l === 'en'
                ? `${n} ${n === 1 ? 'run' : 'runs'} finished OK but skipped rows`
                : `${n}次运行成功但有跳过的记录`;
        return l === 'zh' ? `${g.lavoro}：${che}` : `${g.lavoro}: ${che}`;
    }
    const che = l === 'it'
        ? `${n} ${n === 1 ? 'giro fallito' : 'giri falliti'}`
        : l === 'en'
            ? `${n} failed ${n === 1 ? 'run' : 'runs'}`
            : `${n}次运行失败`;
    return l === 'zh' ? `${g.lavoro}：${che}` : `${g.lavoro}: ${che}`;
}
/**
 * Un numero di ore scritto come lo scriverebbe una persona: `2`, `2,5`, `0,5`.
 *
 * La virgola in italiano e il punto nelle altre due, perché questa stringa finisce dentro una
 * frase e non dentro una tabella: «(2.5 ore)» in un testo italiano si legge come un refuso.
 */
function oreTesto(ore, l) {
    const arrotondato = Math.round(ore * 100) / 100;
    const nudo = String(arrotondato);
    return l === 'it' ? nudo.replace('.', ',') : nudo;
}
/**
 * «dal 10 set al 14 set (5 giorni)», oppure «il 8 ott (2 ore)», nelle tre lingue.
 *
 * ## Perché le ore, e perché erano un difetto
 *
 * Trovato il 2026-10-05 guardando la notifica vera sul telefono: un permesso **dalle 09:00
 * alle 11:00** arrivava annunciato come *«il gio 8 ott (1 giorno)»*. Non era un'imprecisione
 * di stile — era la frase che dice a una persona quanto le è stato approvato, e diceva
 * **otto volte tanto**. Chi la legge sulla schermata di blocco non apre nient'altro.
 *
 * La causa è che `giorni` era l'unica misura che questa funzione conoscesse, e per una
 * richiesta che comincia e finisce lo stesso giorno vale `1` **sempre** — che sia un giorno
 * intero o venti minuti. La differenza fra le due cose non stava qui: stava nel tipo della
 * causale (`unit: DAY | HOUR`), cioè in un'altra tavola, e nessuno gliel'aveva portata.
 *
 * ⚠ **`ore` è facoltativo e questo è voluto.** Tre delle sei specie che passano di qui — le
 * proposte di ferie, i turni — non hanno ore e non potranno mai averne: obbligarle a scrivere
 * `ore: null` sarebbe una chiave in più in tre carichi per un fatto che lì non esiste. Dove
 * invece la richiesta **può** essere oraria, la chiave c'è nel tipo del carico, e il
 * compilatore la pretende.
 *
 * Le ore vincono sul giorno solo quando c'è **un giorno solo**: un permesso orario su due
 * giorni non esiste (`SchermataNuovoGiustificativo` lascia scegliere una data sola quando la
 * causale è a ore), ma se un domani esistesse, «dal … al … (5 giorni)» resta vero mentre
 * «(2 ore)» non direbbe su quali giorni.
 */
function periodo(dal, al, giorni, fuso, l, ore) {
    const a = giornoTesto(dal, fuso, l);
    const b = giornoTesto(al, fuso, l);
    const unSolo = dal.getTime() === al.getTime();
    const aOre = unSolo && typeof ore === 'number' && Number.isFinite(ore) && ore > 0;
    if (l === 'it') {
        if (aOre)
            return `il ${a} (${oreTesto(ore, l)} ${ore === 1 ? 'ora' : 'ore'})`;
        return unSolo ? `il ${a} (1 giorno)` : `dal ${a} al ${b} (${giorni} giorni)`;
    }
    if (l === 'en') {
        if (aOre)
            return `on ${a} (${oreTesto(ore, l)} ${ore === 1 ? 'hour' : 'hours'})`;
        return unSolo ? `on ${a} (1 day)` : `from ${a} to ${b} (${giorni} days)`;
    }
    if (aOre)
        return `${a}（${oreTesto(ore, l)}小时）`;
    return unSolo ? `${a}（1天）` : `${a}至${b}（${giorni}天）`;
}
/**
 * I nomi dei tipi d'anagrafica nelle due lingue che il gestionale non parla.
 *
 * `tipo` arriva dal gestionale, ed e' **italiano**: «Ferie», «Vitto». Fino al 2026-10-31 il
 * modello lo metteva cosi' com'era in tutt'e tre le frasi, e a un dipendente cinese arrivava
 * «已批准：Ferie» (giro 6, S2). L'app quel nome lo traduce gia' per **codice**
 * (`nomeTipoAssenza` in `apps/mobile/src/lingua/anagrafiche.ts`), e queste due tavole dicono
 * le stesse parole dei suoi dizionari: la notifica e la schermata che apre devono chiamare
 * la stessa assenza con lo stesso nome.
 *
 * In italiano vince il nome del gestionale, come nell'app. Un codice che qui non c'e' — un
 * tipo aggiunto in anagrafica dopo — ripiega sul nome: italiano, ma vero.
 */
const NOMI_ASSENZA = {
    [JustificationTypeCode.FERIE]: { en: 'Annual leave', zh: '年假' },
    [JustificationTypeCode.PERMESSO]: { en: 'Paid time off', zh: '带薪事假' },
    [JustificationTypeCode.ROL]: { en: 'Hours in lieu (ROL)', zh: '调休（ROL）' },
    [JustificationTypeCode.MALATTIA]: { en: 'Sick leave', zh: '病假' },
    [JustificationTypeCode.INFORTUNIO]: { en: 'Injury at work', zh: '工伤' },
    [JustificationTypeCode.CONGEDO]: { en: 'Parental leave', zh: '育儿假' },
    [JustificationTypeCode.LEGGE104]: { en: 'Carer’s leave (Law 104)', zh: '护理假（104号法）' },
    [JustificationTypeCode.TRASFERTA]: { en: 'Business trip', zh: '出差' },
    [JustificationTypeCode.SMARTWORKING]: { en: 'Remote work', zh: '远程办公' },
};
const NOMI_SPESA = {
    [ExpenseNoteTypeCode.VITTO]: { en: 'Meals', zh: '餐费' },
    [ExpenseNoteTypeCode.ALLOGGIO]: { en: 'Accommodation', zh: '住宿费' },
    [ExpenseNoteTypeCode.CARBURANTE]: { en: 'Fuel', zh: '油费' },
    [ExpenseNoteTypeCode.PEDAGGI]: { en: 'Tolls and parking', zh: '过路费和停车费' },
    [ExpenseNoteTypeCode.TRASPORTI]: { en: 'Trains, flights, taxis', zh: '火车、飞机、出租车' },
    [ExpenseNoteTypeCode.RAPPRESENTANZA]: { en: 'Client entertainment', zh: '业务招待费' },
    [ExpenseNoteTypeCode.MATERIALI]: { en: 'Materials and supplies', zh: '材料和用品' },
    [ExpenseNoteTypeCode.ALTRO]: { en: 'Other', zh: '其他' },
};
function nomeNellaLingua(tavola, nome, codice, l) {
    if (l === 'it' || codice == null)
        return nome;
    return tavola[codice]?.[l] ?? nome;
}
/** «Ferie» in italiano, «Annual leave» in inglese, «年假» in cinese: per codice. */
function nomeAssenzaNellaLingua(nome, codice, l) {
    return nomeNellaLingua(NOMI_ASSENZA, nome, codice, l);
}
/** «Vitto» / «Meals» / «餐费». */
function nomeSpesaNellaLingua(nome, codice, l) {
    return nomeNellaLingua(NOMI_SPESA, nome, codice, l);
}
/**
 * Il tipo di assenza quando si può dire, e la dichiarazione che c'è ma non esce quando no.
 *
 * `null` non diventa «Assenza» e basta, e la coda vale la riga in più: senza, «Assenza dal
 * 10 al 14» sarebbe indistinguibile da un tipo che si chiama davvero così, e chi legge
 * crederebbe di aver visto il motivo. Dice la sola cosa vera — che il motivo c'è e che non
 * è qui — e dal 2026-09-15 è **l'unico posto** in cui quella regola è scritta (sweep legale
 * R3). Prima stava anche in `descriviAssenza` lato server, che componeva la stessa frase in
 * italiano soltanto: quando l'ultima notifica di quel dominio è passata ai modelli, quella
 * funzione non la chiamava più nessuno ed è stata tolta invece di lasciarla lì a invecchiare
 * come seconda verità (§116). Velare in italiano e dire in inglese non velerebbe niente.
 */
function assenza(tipo, l, codice) {
    if (tipo !== null)
        return nomeAssenzaNellaLingua(tipo, codice, l);
    return l === 'it'
        ? 'Assenza (motivo riservato)'
        : l === 'en'
            ? 'Absence (reason withheld)'
            : '缺勤（原因保密）';
}
/**
 * Una persona che manca, con la causale accanto quando si può dire: «ROSSI MARIO (Ferie)».
 *
 * Il nome resta **come sta in anagrafica**, in maiuscolo. È esattamente la forma che il
 * traduttore automatico cancella — misurato in testa a questo file — e la ragione per cui
 * questi elenchi non passano di là. Qui il nome è un dato che attraversa la frase, non una
 * parola da tradurre.
 *
 * `tipo: null` vuol dire che la causale è un dato particolare (malattia, infortunio, congedo,
 * legge 104: `JustificationType.sensitive`) e al responsabile non esce. Non si scrive «motivo
 * riservato» come fa {@link assenza}: là la frase parla di **una** richiesta e quella coda
 * dice l'unica cosa vera; qui la stessa coda, ripetuta su tre nomi di seguito, comincerebbe a
 * indicare *chi* ha un motivo riservato, che è precisamente il fatto che si sta velando.
 * «(assente)» dice quello che il responsabile deve sapere e basta.
 */
/**
 * «dal 9 set all'11 set», senza il conto dei giorni.
 *
 * {@link periodo} il conto ce l'ha, e là serve: parla di **una** richiesta, e «5 giorni» è
 * quello che si scala dal residuo. Qui i periodi stanno dentro una parentesi accanto al nome,
 * tre o quattro per riga, e il conto ci metterebbe una seconda parentesi dentro la prima —
 * «(Ferie, dal 9 set all'11 set (3 giorni))» — che è illeggibile prima ancora di essere
 * inutile: chi legge un elenco di assenti guarda le date, non l'aritmetica.
 */
function finestra(dal, al, fuso, l) {
    const a = giornoTesto(dal, fuso, l);
    const b = giornoTesto(al, fuso, l);
    const unSolo = dal.getTime() === al.getTime();
    if (l === 'it')
        return unSolo ? `il ${a}` : `dal ${a} al ${b}`;
    if (l === 'en')
        return unSolo ? `on ${a}` : `from ${a} to ${b}`;
    return unSolo ? a : `${a}至${b}`;
}
/**
 * «e altri 3», «e un altro»: la coda di un elenco tagliato, o niente se non è stato tagliato.
 *
 * La specie non è un vezzo grammaticale: in italiano decide il genere («e altre due
 * spedizioni»), e in cinese decide il **classificatore** — le persone si contano con 人 e le
 * spedizioni con 单. Un classificatore sbagliato non è un refuso, è la frase che dice
 * «e altre due persone» in fondo a un elenco di colli.
 */
function eAltri(resto, l, specie = 'persone') {
    if (resto <= 0)
        return '';
    const donna = specie === 'spedizioni';
    if (l === 'it') {
        if (resto === 1)
            return donna ? " e un'altra" : ' e un altro';
        return donna ? ` e altre ${resto}` : ` e altri ${resto}`;
    }
    if (l === 'en')
        return resto === 1 ? ' and 1 more' : ` and ${resto} more`;
    return `等${resto}${donna ? '单' : '人'}`;
}
function chiVia(chi, tipo, l, codice, coda = '') {
    const dentro = tipo !== null
        ? nomeAssenzaNellaLingua(tipo, codice, l)
        : l === 'it'
            ? 'assente'
            : l === 'en'
                ? 'absent'
                : '缺勤';
    const testo = coda === '' ? dentro : `${dentro}${l === 'zh' ? '，' : ', '}${coda}`;
    return l === 'zh' ? `${chi}（${testo}）` : `${chi} (${testo})`;
}
const PRIORITA = {
    bassa: { it: 'bassa', en: 'low', zh: '低' },
    normale: { it: 'normale', en: 'normal', zh: '普通' },
    alta: { it: 'alta', en: 'high', zh: '高' },
    urgente: { it: 'urgente', en: 'urgent', zh: '紧急' },
};
/**
 * «24,50 €» / «€24.50» / «€24.50»: un importo in euro, scritto come si scrive in quella lingua.
 *
 * La stessa ragione delle date: «24,50 €» dato a un traduttore è un numero che può tornare
 * arrotondato o col separatore sbagliato. `Intl` mette la valuta al posto e col separatore
 * giusti — la virgola decimale in italiano, il punto in inglese e in cinese.
 */
function importoTesto(euro, l) {
    return new Intl.NumberFormat(LOCALE[l], { style: 'currency', currency: 'EUR' }).format(euro);
}
/**
 * «2× Camice bianco (M)、1× Scarpe (43)» — i capi di una richiesta divise, in una riga.
 *
 * Il nome del capo è una voce di catalogo (come una sala) e non si traduce: attraversa la
 * frase come un dato. In cinese la virgola d'elenco è 、 e le parentesi della taglia sono （）.
 */
function elencoCapi(capi, l) {
    const sep = l === 'zh' ? '、' : ', ';
    return capi
        .map((c) => {
        const taglia = c.taglia ? (l === 'zh' ? `（${c.taglia}）` : ` (${c.taglia})`) : '';
        return `${c.quantita}× ${c.nome}${taglia}`;
    })
        .join(sep);
}
/**
 * L'etichetta di un genere di documento paghe, per lingua.
 *
 * Tredicesima, quattordicesima, Certificazione Unica e TFR sono concetti del cedolino
 * italiano: l'inglese e il cinese usano l'equivalente funzionale corrente («13th-month pay»,
 * «年度税单»), non una traduzione parola per parola. È una scelta di terminologia, scritta qui
 * una volta per poterla correggere in un posto solo.
 */
const TIPO_BUSTA = {
    mensile: { it: 'Busta paga', en: 'Payslip', zh: '工资单' },
    tredicesima: { it: 'Tredicesima', en: '13th-month pay', zh: '第十三个月工资' },
    quattordicesima: { it: 'Quattordicesima', en: '14th-month pay', zh: '第十四个月工资' },
    premio: { it: 'Premio', en: 'Bonus', zh: '奖金' },
    cu: { it: 'Certificazione Unica', en: 'Annual tax statement', zh: '年度税单' },
    tfr: { it: 'TFR e cessazione', en: 'Severance', zh: '离职金' },
};
/** «Busta paga marzo 2026» / «Payslip March 2026» / «2026年3月工资单». */
function etichettaBusta(p, l) {
    const tipo = TIPO_BUSTA[p.tipo][l];
    if (p.tipo === 'mensile' && p.mese) {
        // Il mese in parole, nella lingua giusta, dal fuso UTC: qui interessa il nome del mese,
        // non un istante, e ancorarlo a UTC lo tiene stabile a qualunque ora giri il server.
        const mese = new Intl.DateTimeFormat(LOCALE[l], { month: 'long', timeZone: 'UTC' }).format(new Date(Date.UTC(p.anno, p.mese - 1, 1)));
        return l === 'zh' ? `${p.anno}年${p.mese}月${tipo}` : `${tipo} ${mese} ${p.anno}`;
    }
    return l === 'zh' ? `${p.anno}年${tipo}` : `${tipo} ${p.anno}`;
}
/**
 * «nel cedolino di ottobre 2026» / «in the October 2026 payslip» / «计入2026年10月的工资单»:
 * il cedolino su cui viaggia un rimborso, detto a parole e con la sua preposizione.
 *
 * Il periodo arriva come codice (`PERIODO_PAGA`: `AAAA-01`…`AAAA-12`, `AAAA-13` tredicesima,
 * `AAAA-14` quattordicesima, `AAAA-CU`), e la push lo stampava così com'era: «nel cedolino di
 * 2026-13», mentre la scheda dell'app dice «Tredicesima 2026» (`periodoBusta`). Un codice che
 * non si riconosce resta com'è: meglio il codice di un buco.
 */
function cedolinoDelRimborso(codice, l) {
    const anno = Number(codice.slice(0, 4));
    const coda = codice.slice(5);
    const grezzo = l === 'it' ? `nel cedolino di ${codice}` : l === 'en' ? `in the ${codice} payslip` : `计入${codice}的工资单`;
    if (codice[4] !== '-' || !Number.isInteger(anno))
        return grezzo;
    const speciale = { '13': 'tredicesima', '14': 'quattordicesima', CU: 'cu' };
    const tipo = speciale[coda];
    if (tipo) {
        const nome = TIPO_BUSTA[tipo][l];
        if (l === 'it') {
            return tipo === 'cu' ? `nella ${nome} ${anno}` : `nel cedolino della ${nome.toLowerCase()} ${anno}`;
        }
        // «Annual tax statement» è un'etichetta: dentro la frase va minuscola.
        if (l === 'en')
            return `in the ${anno} ${nome.charAt(0).toLowerCase()}${nome.slice(1)}`;
        return `计入${anno}年${nome}`;
    }
    const mese = Number(coda);
    if (!/^\d{2}$/.test(coda) || mese < 1 || mese > 12)
        return grezzo;
    if (l === 'zh')
        return `计入${anno}年${mese}月的工资单`;
    const nomeMese = new Intl.DateTimeFormat(LOCALE[l], { month: 'long', timeZone: 'UTC' }).format(new Date(Date.UTC(anno, mese - 1, 1)));
    return l === 'it' ? `nel cedolino di ${nomeMese} ${anno}` : `in the ${nomeMese} ${anno} payslip`;
}
/**
 * «Prato → Milano. » / «Prato → Milano。» / «» — la rotta di un viaggio col separatore già
 * attaccato, o niente quando manca un capo (invece di «undefined → undefined»).
 *
 * Il separatore è dentro: in cinese il punto è 。 e non vuole lo spazio dopo, nelle altre due
 * è «. ». Così chi la usa scrive `${rotta(...)}Apri…` e la frase è giusta in tutte e tre anche
 * quando la rotta non c'è.
 */
function rotta(partenza, arrivo, l) {
    if (!(partenza && arrivo))
        return '';
    return l === 'zh' ? `${partenza} → ${arrivo}。` : `${partenza} → ${arrivo}. `;
}
/**
 * Quante pratiche scadute si dicono per esteso in un avviso solo ([[K664]]). Le altre restano
 * col codice soltanto: sei righe si leggono su un telefono, trenta no, e chi ne ha trenta
 * scadute insieme ha un problema che un avviso non risolve.
 */
const PRATICHE_PER_ESTESO = 6;
/** «Spedizioni scadute: SP-…, da T.A.T. srl a COLLE: inverter. Chiesta da …, merce pronta il ven 16 ott.» */
function spedizioniScadute(codici, dettagli, fuso, l) {
    if (!codici.length)
        return null;
    const perCodice = new Map((dettagli ?? []).map((d) => [d.codice, d]));
    const voci = codici.slice(0, PRATICHE_PER_ESTESO).map((c) => {
        const d = perCodice.get(c);
        if (!d)
            return l === 'zh' ? `${c}。` : `${c}.`;
        const giorno = d.pronta ? giornoTesto(d.pronta, fuso, l) : null;
        if (l === 'it') {
            const dove = d.da && d.a ? `, da ${d.da} a ${d.a}` : '';
            const cosa = d.cosa ? `: ${d.cosa}` : '';
            const chi = [d.chi ? `chiesta da ${d.chi}` : null, giorno ? `merce pronta il ${giorno}` : null]
                .filter(Boolean)
                .join(', ');
            return `${c}${dove}${cosa}.${chi ? ` ${maiuscola(chi)}.` : ''}`;
        }
        if (l === 'en') {
            const dove = d.da && d.a ? `, from ${d.da} to ${d.a}` : '';
            const cosa = d.cosa ? `: ${d.cosa}` : '';
            const chi = [d.chi ? `requested by ${d.chi}` : null, giorno ? `goods ready on ${giorno}` : null]
                .filter(Boolean)
                .join(', ');
            return `${c}${dove}${cosa}.${chi ? ` ${maiuscola(chi)}.` : ''}`;
        }
        const dove = d.da && d.a ? `，${d.da} → ${d.a}` : '';
        const cosa = d.cosa ? `：${d.cosa}` : '';
        const chi = [d.chi ? `申请人：${d.chi}` : null, giorno ? `货物备妥：${giorno}` : null]
            .filter(Boolean)
            .join('，');
        return `${c}${dove}${cosa}。${chi ? `${chi}。` : ''}`;
    });
    const coda = eAltri(codici.length - voci.length, l, 'spedizioni');
    const uno = codici.length === 1;
    if (l === 'it') {
        return `${uno ? 'Spedizione scaduta' : 'Spedizioni scadute'}: ${voci.join(' ')}${coda ? `${coda.trim().replace(/^e /, 'E ')}.` : ''}`;
    }
    if (l === 'en') {
        return `${uno ? 'Expired shipment' : 'Expired shipments'}: ${voci.join(' ')}${coda ? ` ${maiuscola(coda.trim())}.` : ''}`;
    }
    return `已过期运单：${voci.join('')}${coda ? `${coda}。` : ''}`;
}
/** «Viaggio scaduto: VG-… del lun 19 ott, autista …: 2 fermate rimaste aperte, 1 spedizione tornata in coda (SP-…).» */
function viaggiScaduti(codici, dettagli, fuso, l) {
    if (!codici.length)
        return null;
    const perCodice = new Map((dettagli ?? []).map((d) => [d.codice, d]));
    const voci = codici.slice(0, PRATICHE_PER_ESTESO).map((c) => {
        const d = perCodice.get(c);
        if (!d)
            return l === 'zh' ? `${c}。` : `${c}.`;
        const giorno = giornoTesto(d.giorno, fuso, l);
        const n = d.tappeAperte;
        const m = d.tornate.length;
        const quali = d.tornate.join(l === 'zh' ? '、' : ', ');
        if (l === 'it') {
            const autista = d.autista ? `, autista ${d.autista}` : ', senza autista';
            const tappe = n === 0 ? 'nessuna fermata rimasta aperta' : n === 1 ? '1 fermata rimasta aperta' : `${n} fermate rimaste aperte`;
            const coda = m === 0 ? '' : `, ${m === 1 ? '1 spedizione tornata' : `${m} spedizioni tornate`} in coda (${quali})`;
            return `${c} del ${giorno}${autista}: ${tappe}${coda}.`;
        }
        if (l === 'en') {
            const autista = d.autista ? `, driver ${d.autista}` : ', no driver';
            const tappe = n === 0 ? 'no stops left open' : `${n} stop${n === 1 ? '' : 's'} left open`;
            const coda = m === 0 ? '' : `, ${m} shipment${m === 1 ? '' : 's'} back in the queue (${quali})`;
            return `${c} on ${giorno}${autista}: ${tappe}${coda}.`;
        }
        const autista = d.autista ? `，司机${d.autista}` : '，无司机';
        const tappe = n === 0 ? '无未完成站点' : `${n}个站点未完成`;
        const coda = m === 0 ? '' : `，${m}单退回队列（${quali}）`;
        return `${c}（${giorno}${autista}）：${tappe}${coda}。`;
    });
    const resto = codici.length - voci.length;
    const uno = codici.length === 1;
    if (l === 'it') {
        return `${uno ? 'Viaggio scaduto' : 'Viaggi scaduti'}: ${voci.join(' ')}${resto ? ` E ${resto === 1 ? 'un altro' : `altri ${resto}`}.` : ''}`;
    }
    if (l === 'en') {
        return `${uno ? 'Expired trip' : 'Expired trips'}: ${voci.join(' ')}${resto ? ` And ${resto} more.` : ''}`;
    }
    return `已过期行程：${voci.join('')}${resto ? `等${resto}项。` : ''}`;
}
/** «餐费（8月12日，24.50 €）» — la nota spese in una riga: tipo, giorno, importo. */
function notaTesto(tipo, giorno, fuso, importo, l) {
    const g = giornoTesto(giorno, fuso, l);
    const soldi = importoTesto(importo, l);
    if (l === 'it')
        return `${tipo} del ${g}, ${soldi}`;
    if (l === 'en')
        return `${tipo} on ${g}, ${soldi}`;
    return `${tipo}（${g}，${soldi}）`;
}
const MODELLI = {
    /*
      Il **quando e il dove salgono nel sottotitolo**, e il corpo si accorcia a quel che resta.
  
      E' la riga su cui si decide se aprire: «lun 28 set, 14:30–15:30, sala Tintoria» si legge
      sulla schermata di blocco senza toccare niente, mentre prima stava in testa a un corpo che
      su due righe finiva troncato a meta' proprio li'. Quello che resta nel corpo — quanti altri
      sono invitati — e' informazione buona e **seconda**.
    */
    'riunione.invito': (p, l) => {
        const quando = quandoTesto(p.inizio, p.fuso, l);
        const fine = oraTesto(p.fine, p.fuso, l);
        const sottotitolo = `${quando}–${fine}${dove(p.sala, l, p.luogo)}`;
        if (l === 'it') {
            return {
                titolo: `${p.organizzatore} ti invita: ${p.titolo}`,
                sottotitolo,
                corpo: p.altri === 0
                    ? 'Sei l’unico invitato.'
                    : p.altri === 1
                        ? 'C’è un altro invitato.'
                        : `Ci sono altri ${p.altri} invitati.`,
            };
        }
        if (l === 'en') {
            return {
                titolo: `${p.organizzatore} invites you: ${p.titolo}`,
                sottotitolo,
                corpo: p.altri === 0
                    ? 'You are the only guest.'
                    : p.altri === 1
                        ? 'One other person is invited.'
                        : `${p.altri} other people are invited.`,
            };
        }
        return {
            titolo: `${p.organizzatore}邀请你：${p.titolo}`,
            sottotitolo,
            corpo: p.altri === 0 ? '只邀请了你一人。' : `另有${p.altri}人受邀。`,
        };
    },
    'riunione.spostata': (p, l) => {
        const prima = quandoTesto(p.inizioPrima, p.fuso, l);
        const adesso = quandoTesto(p.inizio, p.fuso, l);
        const fine = oraTesto(p.fine, p.fuso, l);
        if (l === 'it') {
            return {
                titolo: `Spostata: ${p.titolo}`,
                corpo: `Adesso ${adesso}–${fine}${dove(p.sala, l, p.luogo)}, non più ${prima}. L’ha spostata ${p.chi}. Rispondi di nuovo.`,
            };
        }
        if (l === 'en') {
            return {
                titolo: `Moved: ${p.titolo}`,
                corpo: `Now ${adesso}–${fine}${dove(p.sala, l, p.luogo)}, no longer ${prima}. Moved by ${p.chi}. Please reply again.`,
            };
        }
        return {
            titolo: `已改期：${p.titolo}`,
            corpo: `现为${adesso}–${fine}${dove(p.sala, l, p.luogo)}，原定${prima}。由${p.chi}改期。请重新回复。`,
        };
    },
    // Al titolare: la stessa notizia **senza la domanda** — niente «Rispondi di nuovo», la
    // riunione e' sua (avvisoAlTitolareSenzaTasti).
    'riunione.spostataTitolare': (p, l) => {
        const prima = quandoTesto(p.inizioPrima, p.fuso, l);
        const adesso = quandoTesto(p.inizio, p.fuso, l);
        const fine = oraTesto(p.fine, p.fuso, l);
        if (l === 'it') {
            return {
                titolo: `${p.chi} ha spostato la tua riunione: ${p.titolo}`,
                corpo: `Adesso ${adesso}–${fine}${dove(p.sala, l, p.luogo)}, non più ${prima}. Gli invitati devono rispondere di nuovo.`,
            };
        }
        if (l === 'en') {
            return {
                titolo: `${p.chi} moved your meeting: ${p.titolo}`,
                corpo: `Now ${adesso}–${fine}${dove(p.sala, l, p.luogo)}, no longer ${prima}. Guests are asked to reply again.`,
            };
        }
        return {
            titolo: `${p.chi}改期了你的会议：${p.titolo}`,
            corpo: `现为${adesso}–${fine}${dove(p.sala, l, p.luogo)}，原定${prima}。受邀者需重新回复。`,
        };
    },
    'riunione.cambiataSala': (pp, l) => {
        // Il luogo personale vale come una sala in questa frase: è dove si va ([[K667]]).
        const p = {
            ...pp,
            sala: pp.sala ?? pp.luogo ?? null,
            salaPrima: pp.salaPrima ?? pp.luogoPrima ?? null,
        };
        const quando = quandoTesto(p.inizio, p.fuso, l);
        /*
          Il titolo dice **la sala**, non «cambio sala»: chi legge la notifica sulla schermata
          bloccata deve sapere dove andare senza aprire niente. E il corpo tiene l'ora, perché
          una notifica che parla di una riunione senza dire quando obbliga ad aprire l'app per
          capire se è quella di stamattina o quella di giovedì.
        */
        if (l === 'it') {
            if (p.sala === null) {
                return {
                    titolo: `Senza sala: ${p.titolo}`,
                    corpo: `${quando} non è più in ${p.salaPrima ?? 'sala'}. L’ha cambiata ${p.chi}. L’ora resta questa.`,
                };
            }
            return {
                titolo: `In ${p.sala}: ${p.titolo}`,
                corpo: `${quando}, adesso in ${p.sala}${p.salaPrima !== null ? ` e non più in ${p.salaPrima}` : ''}. L’ha cambiata ${p.chi}. L’ora resta questa.`,
            };
        }
        if (l === 'en') {
            if (p.sala === null) {
                return {
                    titolo: `No room: ${p.titolo}`,
                    corpo: `${quando} is no longer in ${p.salaPrima ?? 'a room'}. Changed by ${p.chi}. Same time.`,
                };
            }
            return {
                titolo: `In ${p.sala}: ${p.titolo}`,
                corpo: `${quando}, now in ${p.sala}${p.salaPrima !== null ? `, no longer in ${p.salaPrima}` : ''}. Changed by ${p.chi}. Same time.`,
            };
        }
        if (p.sala === null) {
            return {
                titolo: `无会议室：${p.titolo}`,
                corpo: `${quando}不再在${p.salaPrima ?? '会议室'}。由${p.chi}更改。时间不变。`,
            };
        }
        return {
            titolo: `在${p.sala}：${p.titolo}`,
            corpo: `${quando}，现在${p.sala}${p.salaPrima !== null ? `，不再在${p.salaPrima}` : ''}。由${p.chi}更改。时间不变。`,
        };
    },
    'riunione.annullata': (p, l) => {
        const quando = quandoTesto(p.inizio, p.fuso, l);
        if (l === 'it') {
            return {
                titolo: `Annullata: ${p.titolo}`,
                corpo: `Era ${quando}. L’ha annullata ${p.chi}.` + (p.motivo ? ` Motivo: ${p.motivo}` : ''),
            };
        }
        if (l === 'en') {
            return {
                titolo: `Cancelled: ${p.titolo}`,
                corpo: `It was ${quando}. Cancelled by ${p.chi}.` + (p.motivo ? ` Reason: ${p.motivo}` : ''),
            };
        }
        return {
            titolo: `已取消：${p.titolo}`,
            corpo: `原定${quando}。由${p.chi}取消。` + (p.motivo ? `原因：${p.motivo}` : ''),
        };
    },
    /*
      L'ora e la sala nel sottotitolo, e nel corpo **quanto manca**.
  
      E' l'unica specie delle riunioni che buca il Non Disturbare, e allora la riga che si legge
      per prima deve dire dove andare, non ripetere che sta per cominciare — quello lo dice gia'
      il titolo.
    */
    'riunione.fraPoco': (p, l) => {
        const ora = oraTesto(p.inizio, p.fuso, l);
        const sottotitolo = `${ora}${dove(p.sala, l, p.luogo)}`;
        if (l === 'it') {
            return { titolo: `Fra poco: ${p.titolo}`, sottotitolo, corpo: `Comincia alle ${ora}.` };
        }
        if (l === 'en') {
            return { titolo: `Starting soon: ${p.titolo}`, sottotitolo, corpo: `Starts at ${ora}.` };
        }
        return { titolo: `即将开始：${p.titolo}`, sottotitolo, corpo: `${ora}开始。` };
    },
    /*
      Vale per due momenti diversi della stessa pratica: la richiesta appena presentata
      (`oreAttesa: 0`) e il sollecito del giorno dopo. Sono la stessa domanda a distanza di
      ore, e un modello solo è ciò che impedisce alle due copie di raccontare la stessa
      pratica in due modi.
  
      A zero ore la frase cambia: «aspetta da 0 ore» è un modo di dire «adesso» che nessuno
      scriverebbe, e su una notifica appena arrivata suona come un numero rotto.
    */
    /*
      **Che assenza e per quali giorni** sale nel sottotitolo: e' quello che fa decidere se
      aprire adesso o dopo pranzo. «Ferie 14–18 set (5 gg)» si legge sulla schermata di blocco
      senza toccare niente; il corpo resta a dire **da quanto aspetta**, che e' la ragione per
      cui la notifica e' arrivata proprio oggi.
    */
    'firma.attesa': (p, l) => {
        const per = periodo(p.dal, p.al, p.giorni, p.fuso, l, p.ore);
        const appena = Math.round(p.oreAttesa) < 1;
        const da = daQuantoTesto(p.oreAttesa, l);
        const sottotitolo = `${assenza(p.tipo, l, p.codiceTipo)} ${per}`;
        if (l === 'it') {
            const attesa = p.livello === 1
                ? 'In attesa della prima firma'
                : 'La prima firma c’è, in attesa della seconda';
            return {
                titolo: `Da firmare: ${p.dipendente}`,
                sottotitolo,
                corpo: `${attesa}${appena ? ': è appena arrivata.' : ` da ${da}.`}`,
            };
        }
        if (l === 'en') {
            const attesa = p.livello === 1
                ? 'Waiting for the first signature'
                : 'First signature done, waiting for the second';
            return {
                titolo: `Waiting for your signature: ${p.dipendente}`,
                sottotitolo,
                corpo: `${attesa}${appena ? ': it has just arrived.' : ` for ${da}.`}`,
            };
        }
        const attesa = p.livello === 1 ? '等待第一签批' : '第一签批已完成，等待第二签批';
        return {
            titolo: `待你签批：${p.dipendente}`,
            sottotitolo,
            corpo: `${attesa}${appena ? '：刚刚提交。' : `已${da}。`}`,
        };
    },
    'firma.riserve': (p, l) => {
        const per = periodo(p.dal, p.al, p.giorni, p.fuso, l, p.ore);
        const da = daQuantoTesto(p.oreAttesa, l);
        if (l === 'it') {
            return {
                titolo: `In attesa da ${da}: ${p.dipendente}`,
                corpo: `${assenza(p.tipo, l, p.codiceTipo)} ${per}. Da adesso puoi firmarla anche tu, al posto di chi non l’ha fatto.`,
            };
        }
        if (l === 'en') {
            return {
                titolo: `Waiting for ${da}: ${p.dipendente}`,
                corpo: `${assenza(p.tipo, l, p.codiceTipo)} ${per}. From now on you can sign it too, in place of whoever has not.`,
            };
        }
        return {
            titolo: `已等待${da}：${p.dipendente}`,
            corpo: `${assenza(p.tipo, l, p.codiceTipo)}${per}。从现在起你也可以代为签批。`,
        };
    },
    'firma.dimenticata': (p, l) => {
        const per = periodo(p.dal, p.al, p.giorni, p.fuso, l, p.ore);
        const da = daQuantoTesto(p.oreAttesa, l);
        if (l === 'it') {
            return {
                titolo: `Dimenticata da ${da}: ${p.dipendente}`,
                corpo: `${assenza(p.tipo, l, p.codiceTipo)} ${per}. Nessuno della catena l’ha firmata: va deciso da qualcuno.`,
            };
        }
        if (l === 'en') {
            return {
                titolo: `Forgotten for ${da}: ${p.dipendente}`,
                corpo: `${assenza(p.tipo, l, p.codiceTipo)} ${per}. Nobody in the chain has signed it: somebody has to decide.`,
            };
        }
        return {
            titolo: `已被遗忘${da}：${p.dipendente}`,
            corpo: `${assenza(p.tipo, l, p.codiceTipo)}${per}。审批链中无人签批：需要有人处理。`,
        };
    },
    'riepilogo.giornata': (p, l) => {
        const pezzi = [];
        if (p.firme > 0) {
            pezzi.push(l === 'it'
                ? p.firme === 1
                    ? '1 richiesta da firmare'
                    : `${p.firme} richieste da firmare`
                : l === 'en'
                    ? p.firme === 1
                        ? '1 request to sign'
                        : `${p.firme} requests to sign`
                    : `${p.firme}项待签批`);
        }
        if (p.inviti > 0) {
            pezzi.push(l === 'it'
                ? p.inviti === 1
                    ? '1 invito a cui rispondere'
                    : `${p.inviti} inviti a cui rispondere`
                : l === 'en'
                    ? p.inviti === 1
                        ? '1 invitation to answer'
                        : `${p.inviti} invitations to answer`
                    : `${p.inviti}份邀请待回复`);
        }
        // Le proposte, le divise e le note spese aspettano tutte e tre un **suo** gesto, come le
        // firme e gli inviti: stanno prima delle assenze, che sono un quadro e vanno in coda.
        if (p.proposte > 0) {
            pezzi.push(l === 'it'
                ? p.proposte === 1
                    ? '1 proposta ferie'
                    : `${p.proposte} proposte ferie`
                : l === 'en'
                    ? p.proposte === 1
                        ? '1 leave proposal'
                        : `${p.proposte} leave proposals`
                    : `${p.proposte}项休假提议`);
        }
        if (p.divise > 0) {
            pezzi.push(l === 'it'
                ? p.divise === 1
                    ? '1 richiesta divise'
                    : `${p.divise} richieste divise`
                : l === 'en'
                    ? p.divise === 1
                        ? '1 uniform request'
                        : `${p.divise} uniform requests`
                    : `${p.divise}项工装申请`);
        }
        if (p.noteSpese > 0) {
            pezzi.push(l === 'it'
                ? p.noteSpese === 1
                    ? '1 nota spese da rimborsare'
                    : `${p.noteSpese} note spese da rimborsare`
                : l === 'en'
                    ? p.noteSpese === 1
                        ? '1 expense note to refund'
                        : `${p.noteSpese} expense notes to refund`
                    : `${p.noteSpese}张待报销单`);
        }
        // Le assenze del reparto vanno in coda al titolo e non in testa, di proposito: le prime
        // due famiglie aspettano un **suo** gesto, questa no — è un quadro. Chi legge la riga
        // sulla schermata di blocco deve trovare per primo quello che è fermo per colpa sua.
        if (p.assentiOggi > 0) {
            pezzi.push(l === 'it'
                ? p.assentiOggi === 1
                    ? '1 assente oggi'
                    : `${p.assentiOggi} assenti oggi`
                : l === 'en'
                    ? p.assentiOggi === 1
                        ? '1 away today'
                        : `${p.assentiOggi} away today`
                    : `今日${p.assentiOggi}人缺勤`);
        }
        // `assentiInArrivoNelTitolo` (caccia ai difetti 2, giro 14): chi aveva da sapere **solo**
        // un'assenza in arrivo riceveva il riepilogo col titolo vuoto, perché le assenze di poi
        // stavano nel corpo («Poi: …») e mai fra i pezzi del titolo. Vanno in coda, dopo quelle di oggi.
        if (p.assentiPresto > 0) {
            pezzi.push(l === 'it'
                ? p.assentiPresto === 1
                    ? '1 assenza in arrivo'
                    : `${p.assentiPresto} assenze in arrivo`
                : l === 'en'
                    ? p.assentiPresto === 1
                        ? '1 upcoming absence'
                        : `${p.assentiPresto} upcoming absences`
                    : `${p.assentiPresto}项即将缺勤`);
        }
        /*
          La punteggiatura è quella della lingua, non quella dell'italiano riusata.
    
          In cinese i due punti sono `：`, la virgola d'elenco è `、`, il punto è `。` e le
          parentesi sono `（）`: scriverci dentro `:` e `,` latini è il difetto che
          `controlla-dizionari.mjs` insegue nei dizionari della app, e qui — dove i dizionari
          non ci sono — nessun cancello lo guarderebbe. Vale la riga in più: una frase cinese
          punteggiata all'italiana si legge come un testo tradotto male, che è precisamente
          l'impressione che questi modelli esistono per non dare.
        */
        const zh = l === 'zh';
        const duePunti = zh ? '：' : ': ';
        const virgola = zh ? '、' : ', ';
        const punto = zh ? '。' : '.';
        const righe = [];
        if (p.primeFirme.length > 0) {
            const etichetta = l === 'it' ? 'Da firmare' : l === 'en' ? 'To sign' : '待签批';
            const resto = p.firme - p.primeFirme.length;
            const coda = resto <= 0
                ? punto
                : l === 'it'
                    ? ` e altre ${resto}.`
                    : l === 'en'
                        ? ` and ${resto} more.`
                        : `等${resto}项。`;
            righe.push(`${etichetta}${duePunti}${p.primeFirme.join(virgola)}${coda}`);
        }
        if (p.primiInviti.length > 0) {
            const etichetta = l === 'it' ? 'Da rispondere' : l === 'en' ? 'To answer' : '待回复';
            const resto = p.inviti - p.primiInviti.length;
            const coda = resto <= 0
                ? punto
                : l === 'it'
                    ? ` e altri ${resto}.`
                    : l === 'en'
                        ? ` and ${resto} more.`
                        : `等${resto}份。`;
            const voci = p.primiInviti.map((i) => zh
                ? `${i.titolo}（${quandoTesto(i.inizio, p.fuso, l)}）`
                : `${i.titolo} (${quandoTesto(i.inizio, p.fuso, l)})`);
            righe.push(`${etichetta}${duePunti}${voci.join(virgola)}${coda}`);
        }
        if (p.primeProposte.length > 0) {
            const etichetta = l === 'it' ? 'Ferie proposte' : l === 'en' ? 'Proposed leave' : '拟休假';
            const resto = p.proposte - p.primeProposte.length;
            const coda = resto <= 0
                ? punto
                : l === 'it'
                    ? ` e altre ${resto}.`
                    : l === 'en'
                        ? ` and ${resto} more.`
                        : `等${resto}项。`;
            // Chi ha proposto sta nella parentesi accanto al periodo: è un nome proprio, e attraversa
            // la frase come un dato — che è la ragione per cui questi elenchi non si traducono a macchina.
            const voci = p.primeProposte.map((x) => {
                const per = finestra(x.dal, x.al, p.fuso, l);
                return l === 'it'
                    ? `${per} (da ${x.da})`
                    : l === 'en'
                        ? `${per} (from ${x.da})`
                        : `${per}（来自${x.da}）`;
            });
            righe.push(`${etichetta}${duePunti}${voci.join(virgola)}${coda}`);
        }
        if (p.primeDivise.length > 0) {
            const etichetta = l === 'it' ? 'Divise' : l === 'en' ? 'Uniforms' : '工装';
            const resto = p.divise - p.primeDivise.length;
            const coda = resto <= 0
                ? punto
                : l === 'it'
                    ? ` e altre ${resto}.`
                    : l === 'en'
                        ? ` and ${resto} more.`
                        : `等${resto}项。`;
            const voci = p.primeDivise.map((x) => {
                const capi = l === 'it'
                    ? x.capi === 1
                        ? '1 capo'
                        : `${x.capi} capi`
                    : l === 'en'
                        ? x.capi === 1
                            ? '1 item'
                            : `${x.capi} items`
                        : `${x.capi}件`;
                return zh ? `${x.chi}（${capi}）` : `${x.chi} (${capi})`;
            });
            righe.push(`${etichetta}${duePunti}${voci.join(virgola)}${coda}`);
        }
        if (p.primeNoteSpese.length > 0) {
            const etichetta = l === 'it' ? 'Note spese' : l === 'en' ? 'Expenses' : '报销';
            const resto = p.noteSpese - p.primeNoteSpese.length;
            const coda = resto <= 0
                ? punto
                : l === 'it'
                    ? ` e altre ${resto}.`
                    : l === 'en'
                        ? ` and ${resto} more.`
                        : `等${resto}张。`;
            righe.push(`${etichetta}${duePunti}${p.primeNoteSpese.join(virgola)}${coda}`);
        }
        if (p.primiAssentiOggi.length > 0) {
            const etichetta = l === 'it' ? 'Oggi via' : l === 'en' ? 'Away today' : '今日缺勤';
            const coda = `${eAltri(p.assentiOggi - p.primiAssentiOggi.length, l)}${punto}`;
            // «fino a ven 12 set»: il responsabile che legge un martedì deve sapere se rivedrà
            // quella persona domani o la settimana prossima, ed è la sola cosa che gli fa spostare
            // un turno invece di riproporselo il giorno dopo.
            const voci = p.primiAssentiOggi.map((a) => chiVia(a.chi, a.tipo, l, a.codiceTipo, l === 'it'
                ? `fino a ${giornoTesto(a.al, p.fuso, l)}`
                : l === 'en'
                    ? `until ${giornoTesto(a.al, p.fuso, l)}`
                    : `至${giornoTesto(a.al, p.fuso, l)}`));
            righe.push(`${etichetta}${duePunti}${voci.join(virgola)}${coda}`);
        }
        if (p.primiAssentiPresto.length > 0) {
            const etichetta = l === 'it' ? 'Poi' : l === 'en' ? 'Then' : '随后';
            const coda = `${eAltri(p.assentiPresto - p.primiAssentiPresto.length, l)}${punto}`;
            const voci = p.primiAssentiPresto.map((a) => chiVia(a.chi, a.tipo, l, a.codiceTipo, finestra(a.dal, a.al, p.fuso, l)));
            righe.push(`${etichetta}${duePunti}${voci.join(virgola)}${coda}`);
        }
        return {
            // Il punto mediano cinese non vuole spazi intorno: `1 项待签批·1 份邀请待回复`.
            titolo: pezzi.join(zh ? '·' : ' · '),
            /*
              **Una riga per famiglia, separate da un a capo**, e tagliate a cinque.
      
              Prima erano un paragrafo solo: `righe.join(' ')`. La struttura c'era — questo renderer
              costruisce `righe` una per famiglia, «Da firmare: …», «Inviti: …», «Divise: …» — e
              veniva buttata via proprio nella riga di ritorno.
      
              Con gli a capo quella struttura arriva fino allo schermo, e arriva **su tutt'e due i
              sistemi senza chiedere niente a nessuno**: su Android l'avviso espanso disegna le righe
              (`BigTextStyle`/`InboxStyle`), su iPhone il corpo lungo rispetta gli a capo. Soprattutto
              arriva **in tre lingue**, perche' le righe stanno dentro `corpo`, che e' gia' la colonna
              tradotta — un campo nuovo avrebbe voluto due colonne e un secondo giro di traduzione.
      
              **Cinque e non sette**: cinque e' quel che Android disegna in un `InboxStyle`, e una
              sesta riga non sparirebbe con un avviso — sparirebbe e basta. Tagliare qui, dove si
              vede, e' meglio che farsi tagliare la' dove non si vede. L'ordine delle famiglie lo
              decide questo renderer poco sopra, ed e' quello che mette per primo cio' che e' fermo
              per colpa di chi legge.
            */
            corpo: righe.slice(0, RIGHE_RACCOLTA).join('\n'),
        };
    },
    'assenze.settimana': (p, l) => {
        const zh = l === 'zh';
        const duePunti = zh ? '：' : ': ';
        const virgola = zh ? '、' : ', ';
        const dal = giornoTesto(p.dal, p.fuso, l);
        const al = giornoTesto(p.al, p.fuso, l);
        const picco = giornoTesto(p.picco.giorno, p.fuso, l);
        // Un reparto per riga: nome, quante persone mancano su quante ce ne sono, e i primi nomi.
        // Il denominatore c'è perché tre assenti su quattro e tre su quaranta sono due settimane
        // diverse, e un numero solo le fa sembrare la stessa.
        const righe = p.reparti.map((r) => {
            const coda = eAltri(r.persone - r.primi.length, l);
            const voci = r.primi.map((a) => chiVia(a.chi, a.tipo, l, a.codiceTipo, finestra(a.dal, a.al, p.fuso, l)));
            const quanti = zh ? `${r.persone}/${r.organico}人` : `${r.persone}/${r.organico}`;
            return `${r.nome} ${quanti}${duePunti}${voci.join(virgola)}${coda}${zh ? '。' : '.'}`;
        });
        if (l === 'it') {
            return {
                titolo: p.persone === 1
                    ? 'La settimana che comincia: 1 assente'
                    : `La settimana che comincia: ${p.persone} assenti`,
                corpo: `Da ${dal} a ${al}. ${righe.join(' ')} ` +
                    `Il giorno più scoperto è ${picco}, con ${p.picco.assenti} ` +
                    `${p.picco.assenti === 1 ? 'assente' : 'assenti'}.`,
            };
        }
        if (l === 'en') {
            return {
                titolo: p.persone === 1 ? 'The week ahead: 1 away' : `The week ahead: ${p.persone} away`,
                corpo: `From ${dal} to ${al}. ${righe.join(' ')} ` +
                    `The thinnest day is ${picco}, with ${p.picco.assenti} away.`,
            };
        }
        return {
            titolo: `下周：${p.persone}人缺勤`,
            corpo: `${dal}至${al}。${righe.join('')}` +
                `缺勤最多的一天是${picco}，共${p.picco.assenti}人。`,
        };
    },
    'spedizione.autoCompletata': (p, l) => {
        const zh = l === 'zh';
        const ritiro = giornoTesto(p.ritiroPiuVecchio, p.fuso, l);
        const uno = p.quante === 1;
        const elenco = `${p.codici.join(zh ? '、' : ', ')}${eAltri(p.quante - p.codici.length, l, 'spedizioni')}`;
        if (l === 'it') {
            return {
                titolo: uno ? '1 spedizione chiusa da sola' : `${p.quante} spedizioni chiuse da sole`,
                corpo: `${elenco}. ${uno ? 'Ritirata' : 'Ritirate'} dal ${ritiro} e mai ${uno ? 'chiusa' : 'chiuse'} a mano: ` +
                    `dopo ${p.giorni} giorni il sistema ${uno ? 'l’ha data' : 'le ha date'} per ` +
                    `${uno ? 'consegnata' : 'consegnate'}. Se non è così, ${uno ? 'riaprila' : 'riaprile'}.`,
            };
        }
        if (l === 'en') {
            return {
                titolo: uno ? '1 shipment closed automatically' : `${p.quante} shipments closed automatically`,
                corpo: `${elenco}. Picked up from ${ritiro} and never closed by hand: after ${p.giorni} days ` +
                    `the system recorded ${uno ? 'it' : 'them'} as delivered. Reopen if that is not the case.`,
            };
        }
        return {
            titolo: `${p.quante}单运单已自动按送达处理`,
            corpo: `${elenco}。自${ritiro}提货，一直未手动确认：${p.giorni}天后系统按已送达处理。若并非如此，请重新打开。`,
        };
    },
    /*
      Il **periodo** sale nel sottotitolo, ed e' la cosa su cui si risponde: questa notifica porta
      due tasti (Accetto · Rifiuto), e la regola e' che un tasto esiste solo se la notifica mostra
      quello che serve a premerlo. «10–14 ago (5 gg)» e' quel che serve.
    */
    'proposta.ricevuta': (p, l) => {
        const per = periodo(p.dal, p.al, p.giorni, p.fuso, l);
        const sottotitolo = maiuscola(per);
        if (l === 'it') {
            return {
                titolo: `${p.da} ti propone delle ferie`,
                sottotitolo,
                corpo: 'Accetta o rifiuta: finché non rispondi non è ancora una tua richiesta.' +
                    (p.nota ? ` Nota: ${p.nota}` : ''),
            };
        }
        if (l === 'en') {
            return {
                titolo: `${p.da} proposes leave for you`,
                sottotitolo,
                corpo: 'Accept or decline: until you answer it is not yet your request.' +
                    (p.nota ? ` Note: ${p.nota}` : ''),
            };
        }
        return {
            titolo: `${p.da}为你提议休假`,
            sottotitolo: per,
            corpo: `请接受或拒绝：在你回复之前，这还不是你的申请。` + (p.nota ? `备注：${p.nota}` : ''),
        };
    },
    'spedizione.ancoraTua': (p, l) => {
        const presa = giornoTesto(p.presaIl, p.fuso, l);
        if (l === 'it') {
            return {
                titolo: `Hai ancora la spedizione ${p.codice}`,
                corpo: `${p.partenza} → ${p.arrivo}, presa in carico il ${presa}` +
                    `${p.viaggio === null ? ' e non ancora su nessun viaggio' : `, sul viaggio ${p.viaggio}`}. ` +
                    'Finché è tua non la può assegnare nessun altro: se non riesci a farla, chiedi al gestore dei viaggi di toglierti la presa.',
            };
        }
        if (l === 'en') {
            return {
                titolo: `You still have shipment ${p.codice}`,
                corpo: `${p.partenza} → ${p.arrivo}, taken on ${presa}` +
                    `${p.viaggio === null ? ' and not on any trip yet' : `, on trip ${p.viaggio}`}. ` +
                    'While it is yours nobody else can be given it: if you cannot do it, ask the trip manager to remove it from you.',
            };
        }
        return {
            titolo: `运单${p.codice}仍在你名下`,
            corpo: `${p.partenza} → ${p.arrivo}，${presa}接单` +
                `${p.viaggio === null ? '，尚未加入任何行程' : `，在行程${p.viaggio}上`}。` +
                '只要在你名下，别人就无法接手：若无法完成，请让行程管理员把它从你名下撤回。',
        };
    },
    'spedizione.assegnata': (p, l) => {
        const pr = PRIORITA[p.priorita][l];
        const entro = p.entro === null ? null : quandoTesto(p.entro, p.fuso, l);
        if (l === 'it') {
            return {
                titolo: `Spedizione ${p.codice}: da ${p.partenza} a ${p.arrivo}`,
                corpo: (entro ? `Entro ${entro}. ` : '') +
                    `Priorità ${pr}, ${p.colli === 1 ? '1 collo' : `${p.colli} colli`}.`,
            };
        }
        if (l === 'en') {
            return {
                titolo: `Shipment ${p.codice}: ${p.partenza} to ${p.arrivo}`,
                corpo: (entro ? `By ${entro}. ` : '') +
                    `${pr} priority, ${p.colli === 1 ? '1 package' : `${p.colli} packages`}.`,
            };
        }
        return {
            titolo: `运单${p.codice}：${p.partenza}至${p.arrivo}`,
            corpo: (entro ? `${entro}前。` : '') + `优先级${pr}，${p.colli}件。`,
        };
    },
    'trasferta.chiusa': (p, l) => {
        const g = giornoTesto(p.rientro, p.fuso, l);
        if (l === 'it') {
            return {
                titolo: `Trasferta chiusa: ${p.destinazione}`,
                corpo: `Il rientro del ${g} è passato, quindi si è chiusa da sola. Da chiusa non prende più note spese: se non è finita, riaprila.`,
            };
        }
        if (l === 'en') {
            return {
                titolo: `Business trip closed: ${p.destinazione}`,
                corpo: `The return date of ${g} has passed, so it closed by itself. Once closed it takes no more expenses: if it is not over, reopen it.`,
            };
        }
        return {
            titolo: `出差已关闭：${p.destinazione}`,
            corpo: `返回日期${g}已过，因此自动关闭。关闭后不再接受报销：如尚未结束，请重新打开。`,
        };
    },
    'riunione.tolto': (p, l) => {
        const quando = quandoTesto(p.inizio, p.fuso, l);
        if (l === 'it') {
            return {
                titolo: `Non sei più invitato: ${p.titolo}`,
                corpo: `Era ${quando}. Ti ha tolto ${p.chi}. Sparisce dal tuo calendario e non ricevi più aggiornamenti.`,
            };
        }
        if (l === 'en') {
            return {
                titolo: `No longer invited: ${p.titolo}`,
                corpo: `It was ${quando}. ${p.chi} removed you. It leaves your calendar and you get no more updates.`,
            };
        }
        return {
            titolo: `你已不在受邀之列：${p.titolo}`,
            corpo: `原定${quando}。由${p.chi}移除。该会议将从你的日历中消失，你不会再收到更新。`,
        };
    },
    /*
      Il quando nel sottotitolo, come l'invito alle riunioni; per una chiamata «adesso» il corpo
      dice che e' gia' aperta, che e' l'unica cosa che serve sapere per decidere se entrare.
    */
    'videochiamata.invito': (p, l) => {
        const sottotitolo = p.inizio === null
            ? undefined
            : `${quandoTesto(p.inizio, p.fuso, l)}${p.fine === null ? '' : `–${oraTesto(p.fine, p.fuso, l)}`}`;
        if (l === 'it') {
            return {
                titolo: `${p.chi} ti invita in videochiamata: ${p.titolo}`,
                ...(sottotitolo ? { sottotitolo } : {}),
                corpo: p.inizio === null
                    ? 'La chiamata è già aperta: tocca per entrare.'
                    : 'Tocca per entrare: si apre nell’app, o nel browser dal computer.',
            };
        }
        if (l === 'en') {
            return {
                titolo: `${p.chi} invites you to a video call: ${p.titolo}`,
                ...(sottotitolo ? { sottotitolo } : {}),
                corpo: p.inizio === null
                    ? 'The call is already open: tap to join.'
                    : 'Tap to join: it opens in the app, or in the browser on a computer.',
            };
        }
        return {
            titolo: `${p.chi}邀请你参加视频通话：${p.titolo}`,
            ...(sottotitolo ? { sottotitolo } : {}),
            corpo: p.inizio === null ? '通话已开始：点按即可加入。' : '点按即可加入：在应用中打开，电脑上则在浏览器中打开。',
        };
    },
    'videochiamata.bussa': (p, l) => {
        if (l === 'it') {
            return {
                titolo: `${p.nome} chiede di entrare: ${p.titolo}`,
                corpo: 'È in sala d’attesa della videochiamata. Tocca per entrare e farlo passare.',
            };
        }
        if (l === 'en') {
            return {
                titolo: `${p.nome} is asking to join: ${p.titolo}`,
                corpo: 'They are in the waiting room of the video call. Tap to join and let them in.',
            };
        }
        return {
            titolo: `${p.nome}请求加入：${p.titolo}`,
            corpo: '对方正在视频通话的等候室中。点按即可加入并让其进入。',
        };
    },
    'videochiamata.continua': (p, l) => {
        if (l === 'it') {
            return { titolo: `Continua sul telefono: ${p.titolo}`, corpo: 'Tocca per entrare da qui. Il PC lascia la chiamata da solo.' };
        }
        if (l === 'en') {
            return { titolo: `Continue on your phone: ${p.titolo}`, corpo: 'Tap to join from here. The computer leaves the call on its own.' };
        }
        return { titolo: `在手机上继续：${p.titolo}`, corpo: '点按即可在此加入。电脑会自动退出通话。' };
    },
    'videochiamata.squillo': (p, l) => {
        if (l === 'it')
            return { titolo: `${p.chi} ti chiama`, corpo: 'Videochiamata · tocca per rispondere' };
        if (l === 'en')
            return { titolo: `${p.chi} is calling you`, corpo: 'Video call · tap to answer' };
        return { titolo: `${p.chi}正在呼叫你`, corpo: '视频通话 · 点按接听' };
    },
    'videochiamata.persa': (p, l) => {
        const ora = oraTesto(p.quando, p.fuso, l);
        if (l === 'it')
            return { titolo: `Chiamata persa da ${p.chi}`, corpo: `Alle ${ora}. Tocca per richiamare.` };
        if (l === 'en')
            return { titolo: `Missed call from ${p.chi}`, corpo: `At ${ora}. Tap to call back.` };
        return { titolo: `${p.chi}的未接来电`, corpo: `${ora}。点按回拨。` };
    },
    'viaggio.assegnato': (p, l) => {
        const quando = quandoTesto(p.quando, p.fuso, l);
        const mezzo = p.mezzo === null ? '' : ` · ${p.mezzo}`;
        if (l === 'it') {
            return {
                titolo: `Viaggio ${p.codice}: ${p.partenza} → ${p.arrivo}`,
                corpo: `Si parte ${quando}. ${p.tappe === 1 ? '1 tappa' : `${p.tappe} tappe`}${mezzo}.`,
            };
        }
        if (l === 'en') {
            return {
                titolo: `Trip ${p.codice}: ${p.partenza} → ${p.arrivo}`,
                corpo: `Leaves ${quando}. ${p.tappe === 1 ? '1 stop' : `${p.tappe} stops`}${mezzo}.`,
            };
        }
        return {
            titolo: `行程${p.codice}：${p.partenza} → ${p.arrivo}`,
            corpo: `${quando}出发。${p.tappe}个站点${mezzo}。`,
        };
    },
    'turno.cambiato': (p, l) => {
        const g = giornoTesto(p.giorno, p.fuso, l);
        const nomi = {
            mattino: { it: 'mattino', en: 'morning', zh: '早班' },
            pomeriggio: { it: 'pomeriggio', en: 'afternoon', zh: '中班' },
            notte: { it: 'notte', en: 'night', zh: '夜班' },
            misura: { it: 'su misura', en: 'custom', zh: '自定义' },
            tolto: { it: 'tolto', en: 'removed', zh: '已取消' },
        };
        const turno = nomi[p.turno][l];
        const orario = p.dalle !== null && p.alle !== null ? ` ${p.dalle}–${p.alle}` : '';
        if (l === 'it') {
            return {
                titolo: `Turno cambiato: ${g}`,
                corpo: p.turno === 'tolto'
                    ? `${p.reparto}: quel giorno non hai più turno.`
                    : `${p.reparto}: adesso ${turno}${orario}.`,
            };
        }
        if (l === 'en') {
            return {
                titolo: `Shift changed: ${g}`,
                corpo: p.turno === 'tolto'
                    ? `${p.reparto}: you no longer have a shift that day.`
                    : `${p.reparto}: now ${turno}${orario}.`,
            };
        }
        return {
            titolo: `班次已变更：${g}`,
            corpo: p.turno === 'tolto' ? `${p.reparto}：当天你已无班次。` : `${p.reparto}：现为${turno}${orario}。`,
        };
    },
    // Le tre qui sotto sono scritte a mano nelle tre lingue come tutte le notifiche di sistema:
    // il nome del reparto e il testo della nota sono **dati**, e il traduttore automatico su
    // questi li rendeva vuoti o al contrario (K205).
    'turno.giorniCambiati': (p, l) => {
        // I giorni come li legge una persona: «mar, mer e ven», non un elenco separato da virgole
        // con la virgola anche davanti all'ultimo. La `e` finale e' la differenza fra una frase e
        // un tracciato di sistema.
        const nomi = p.giorni.map((g) => giornoTesto(g, p.fuso, l));
        const quanti = nomi.length;
        const elenco = (ultimo) => quanti === 1 ? nomi[0] : `${nomi.slice(0, -1).join(', ')} ${ultimo} ${nomi[quanti - 1]}`;
        if (l === 'it') {
            return {
                titolo: `${quanti} giorni cambiati: ${p.reparto}`,
                corpo: `${elenco('e')}. Apri «I miei turni» per i tuoi.`,
            };
        }
        if (l === 'en') {
            return {
                titolo: `${quanti} days changed: ${p.reparto}`,
                corpo: `${elenco('and')}. Open “My shifts” to see yours.`,
            };
        }
        return {
            titolo: `${quanti}天班次已变更：${p.reparto}`,
            corpo: `${nomi.join('、')}。请打开“我的班次”查看。`,
        };
    },
    'turno.nota': (p, l) => {
        // Una nota di reparto non ha un giorno: `giorno: null` e' «vale per tutta la settimana»,
        // ed e' una notizia diversa da «vale per martedi'».
        const g = p.giorno === null ? null : giornoTesto(p.giorno, p.fuso, l);
        if (l === 'it') {
            return {
                titolo: g === null ? `Nota sui turni: ${p.reparto}` : `Nota per ${g}: ${p.reparto}`,
                corpo: p.testo,
            };
        }
        if (l === 'en') {
            return {
                titolo: g === null ? `Shift note: ${p.reparto}` : `Note for ${g}: ${p.reparto}`,
                corpo: p.testo,
            };
        }
        return {
            titolo: g === null ? `班次备注：${p.reparto}` : `${g}备注：${p.reparto}`,
            corpo: p.testo,
        };
    },
    'turno.ritirati': (p, l) => {
        const da = giornoTesto(p.lunedi, p.fuso, l);
        if (l === 'it') {
            return {
                titolo: `Turni ritirati: ${p.reparto}`,
                corpo: `La settimana del ${da} e' tornata in lavorazione: i turni non sono piu' visibili finche' non escono di nuovo.`,
            };
        }
        if (l === 'en') {
            return {
                titolo: `Shifts withdrawn: ${p.reparto}`,
                corpo: `The week of ${da} is being reworked: the shifts are not visible until they are published again.`,
            };
        }
        return {
            titolo: `班次已撤回：${p.reparto}`,
            corpo: `${da}那周正在重新编排：班次在再次发布前不可见。`,
        };
    },
    'turni.pubblicati': (p, l) => {
        const da = giornoTesto(p.lunedi, p.fuso, l);
        const a = giornoTesto(p.sabato, p.fuso, l);
        if (l === 'it') {
            return {
                titolo: p.ripubblicata
                    ? `Turni cambiati: ${p.reparto}`
                    : `Turni pubblicati: ${p.reparto}`,
                corpo: `Da ${da} a ${a}. ${p.ripubblicata ? 'Erano già usciti: ricontrolla i tuoi.' : 'Apri «I miei turni» per i tuoi.'}`,
            };
        }
        if (l === 'en') {
            return {
                titolo: p.ripubblicata
                    ? `Shifts changed: ${p.reparto}`
                    : `Shifts published: ${p.reparto}`,
                corpo: `From ${da} to ${a}. ${p.ripubblicata ? 'They were already out: check yours again.' : 'Open “My shifts” to see yours.'}`,
            };
        }
        return {
            titolo: p.ripubblicata ? `班次已变更：${p.reparto}` : `班次已发布：${p.reparto}`,
            corpo: `${da}至${a}。${p.ripubblicata ? '此前已发布：请重新查看你的班次。' : '打开“我的班次”查看。'}`,
        };
    },
    'richiesta.decisa': (p, l) => {
        const per = periodo(p.dal, p.al, p.giorni, p.fuso, l, p.ore);
        const ok = p.esito === 'approvata';
        if (l === 'it') {
            return {
                titolo: ok ? `Approvata: ${assenza(p.tipo, l, p.codiceTipo)}` : `Rifiutata: ${assenza(p.tipo, l, p.codiceTipo)}`,
                corpo: `${per}. ${ok ? 'L’ha approvata' : 'L’ha rifiutata'} ${p.chi}.` +
                    (p.motivo ? ` Motivo: ${p.motivo}` : ''),
            };
        }
        if (l === 'en') {
            return {
                titolo: ok ? `Approved: ${assenza(p.tipo, l, p.codiceTipo)}` : `Refused: ${assenza(p.tipo, l, p.codiceTipo)}`,
                corpo: `${per}. ${ok ? 'Approved by' : 'Refused by'} ${p.chi}.` +
                    (p.motivo ? ` Reason: ${p.motivo}` : ''),
            };
        }
        return {
            titolo: ok ? `已批准：${assenza(p.tipo, l, p.codiceTipo)}` : `已驳回：${assenza(p.tipo, l, p.codiceTipo)}`,
            corpo: `${per}。由${p.chi}${ok ? '批准' : '驳回'}。` + (p.motivo ? `原因：${p.motivo}` : ''),
        };
    },
    'richiesta.scaduta': (p, l) => {
        const per = periodo(p.dal, p.al, p.giorni, p.fuso, l, p.ore);
        if (l === 'it') {
            return {
                titolo: `Scaduta: ${assenza(p.tipo, l, p.codiceTipo)}`,
                // Non «è stata rifiutata» e non «nessuno l'ha approvata»: la prima è falsa, la
                // seconda suona come un'accusa a chi doveva firmare. Si dice il fatto — il giorno è
                // passato — e la cosa da fare, perché una notizia che non dice cosa fare è un
                // dispiacere e basta.
                corpo: `${per}. Il periodo è passato senza una risposta, e la richiesta si è chiusa da sé. Se ti serve ancora, va richiesta di nuovo.`,
            };
        }
        if (l === 'en') {
            return {
                titolo: `Expired: ${assenza(p.tipo, l, p.codiceTipo)}`,
                corpo: `${per}. The dates went by without an answer, so the request closed itself. If you still need it, ask again.`,
            };
        }
        return {
            titolo: `已过期：${assenza(p.tipo, l, p.codiceTipo)}`,
            corpo: `${per}。该时间段已过且无人答复，申请已自动关闭。如仍需要，请重新提交。`,
        };
    },
    'richiesta.scadutaPerChiFirma': (p, l) => {
        const per = periodo(p.dal, p.al, p.giorni, p.fuso, l, p.ore);
        const sottotitolo = `${assenza(p.tipo, l, p.codiceTipo)} ${per}`;
        if (l === 'it') {
            return {
                titolo: `Scaduta senza firma: ${p.dipendente}`,
                sottotitolo,
                // Il fatto e la conseguenza, senza accusare nessuno: chi legge e' fra quelli che potevano
                // firmare, e quel che gli serve sapere e' che la richiesta non c'e' piu' — non la trovera'
                // in calendario, e se l'assenza c'e' stata va sistemata altrove.
                corpo: 'Il periodo è passato senza una firma e la richiesta si è chiusa da sé: non è in calendario. Se l’assenza c’è stata, va registrata di nuovo.',
            };
        }
        if (l === 'en') {
            return {
                titolo: `Expired unsigned: ${p.dipendente}`,
                sottotitolo,
                corpo: 'The dates went by without a signature and the request closed itself: it is not in the calendar. If the absence happened, it has to be recorded again.',
            };
        }
        return {
            titolo: `未签批已过期：${p.dipendente}`,
            sottotitolo,
            corpo: '该时间段已过且无人签批，申请已自动关闭，不在日历中。如该缺勤确已发生，需要重新登记。',
        };
    },
    // ------------------------------------------------------------------ divise
    'divise.daApprovare': (p, l) => {
        const capi = elencoCapi(p.capi, l);
        if (l === 'it') {
            const chiusura = p.perResponsabile
                ? 'Attende la tua decisione.'
                : p.corredoIniziale
                    ? 'Approvvigionamento iniziale, in attesa di una decisione.'
                    : 'È in attesa di una decisione.';
            return {
                titolo: `${p.corredoIniziale ? 'Corredo iniziale' : 'Vestiario'} da approvare: ${p.dipendente}`,
                corpo: `${capi}. ${chiusura}`,
            };
        }
        if (l === 'en') {
            const chiusura = p.perResponsabile
                ? 'It needs your decision.'
                : p.corredoIniziale
                    ? 'Initial supply, awaiting a decision.'
                    : 'Awaiting a decision.';
            return {
                titolo: `${p.corredoIniziale ? 'Initial kit' : 'Uniform'} to approve: ${p.dipendente}`,
                corpo: `${capi}. ${chiusura}`,
            };
        }
        const chiusura = p.perResponsabile
            ? '等待你的决定。'
            : p.corredoIniziale
                ? '初始配发，等待决定。'
                : '等待决定。';
        return {
            titolo: `待审批${p.corredoIniziale ? '初始工装' : '工装'}：${p.dipendente}`,
            corpo: `${capi}。${chiusura}`,
        };
    },
    'divise.approvata': (p, l) => {
        const capi = elencoCapi(p.capi, l);
        if (l === 'it') {
            return {
                titolo: 'Richiesta vestiario approvata',
                corpo: `${capi}. È approvata: il magazzino ti avvisa quando è pronta.`,
            };
        }
        if (l === 'en') {
            return {
                titolo: 'Uniform request approved',
                corpo: `${capi}. Approved: the warehouse will let you know when it is ready.`,
            };
        }
        return { titolo: '工装申请已批准', corpo: `${capi}。已批准：备好后仓库会通知你。` };
    },
    'divise.rifiutata': (p, l) => {
        const capi = elencoCapi(p.capi, l);
        if (l === 'it') {
            return { titolo: 'Richiesta vestiario rifiutata', corpo: `${capi}. Motivo: ${p.motivo}` };
        }
        if (l === 'en') {
            return { titolo: 'Uniform request refused', corpo: `${capi}. Reason: ${p.motivo}` };
        }
        return { titolo: '工装申请已驳回', corpo: `${capi}。原因：${p.motivo}` };
    },
    'divise.consegnata': (p, l) => {
        if (l === 'it') {
            return {
                titolo: p.completa ? 'Vestiario consegnato' : 'Vestiario consegnato in parte',
                corpo: p.completa
                    ? `${elencoCapi(p.capi, l)}. Consegnato: la richiesta è chiusa.`
                    : `Consegnato quello che c’era. Manca ancora: ${elencoCapi(p.mancanti, l)}.`,
            };
        }
        if (l === 'en') {
            return {
                titolo: p.completa ? 'Uniform delivered' : 'Uniform partly delivered',
                corpo: p.completa
                    ? `${elencoCapi(p.capi, l)}. Delivered: the request is closed.`
                    : `Delivered what was in stock. Still missing: ${elencoCapi(p.mancanti, l)}.`,
            };
        }
        return {
            titolo: p.completa ? '工装已发放' : '工装部分发放',
            corpo: p.completa
                ? `${elencoCapi(p.capi, l)}。已发放：申请已关闭。`
                : `已发放现有部分。仍缺：${elencoCapi(p.mancanti, l)}。`,
        };
    },
    // ------------------------------------------------------------------ viaggi
    'viaggio.promemoria': (p, l) => {
        const quando = quandoTesto(p.quando, p.fuso, l);
        const rt = rotta(p.partenza, p.arrivo, l);
        if (l === 'it') {
            return {
                titolo: `Viaggio ${p.codice}: si parte ${quando}`,
                corpo: `${rt}Apri il viaggio per confermare: la conferma sei tu che lo leggi. Se non puoi farlo, dillo adesso a chi lo ha pianificato.`,
            };
        }
        if (l === 'en') {
            return {
                titolo: `Trip ${p.codice}: leaving ${quando}`,
                corpo: `${rt}Open the trip to confirm: reading it is your confirmation. If you cannot go, tell whoever planned it now.`,
            };
        }
        return {
            titolo: `行程${p.codice}：${quando}出发`,
            corpo: `${rt}打开行程以确认：你查看即为确认。如无法前往，请立即告知规划人。`,
        };
    },
    'viaggio.senzaAutista': (p, l) => {
        const quando = quandoTesto(p.quando, p.fuso, l);
        const rt = rotta(p.partenza, p.arrivo, l);
        if (l === 'it') {
            return {
                titolo: `Viaggio ${p.codice} senza autista`,
                corpo: `${rt}Parte ${quando} e non ha ancora nessuno che lo guidi.`,
            };
        }
        if (l === 'en') {
            return {
                titolo: `Trip ${p.codice} has no driver`,
                corpo: `${rt}It leaves ${quando} and still has nobody to drive it.`,
            };
        }
        return {
            titolo: `行程${p.codice}无司机`,
            corpo: `${rt}${quando}出发，仍无人驾驶。`,
        };
    },
    'viaggio.sollecito': (p, l) => {
        const quando = quandoTesto(p.quando, p.fuso, l);
        const rt = rotta(p.partenza, p.arrivo, l);
        const motivi = {
            senzaAutista: {
                it: 'non ha ancora un autista',
                en: 'still has no driver',
                zh: '仍无司机',
            },
            nonLetto: {
                it: 'l’autista non ha ancora aperto il promemoria',
                en: 'the driver has not opened the reminder yet',
                zh: '司机尚未查看提醒',
            },
            nonPartito: {
                it: 'il promemoria non è mai partito',
                en: 'the reminder was never sent',
                zh: '提醒从未发出',
            },
        };
        const motivo = motivi[p.motivo][l];
        if (l === 'it') {
            return {
                titolo: `Viaggio ${p.codice}: parte ${quando} e ${motivo}`,
                corpo: `${rt}Mancano meno di ${p.minuti} minuti alla partenza. Riassegnalo o conferma tu che è coperto: il sistema non cambia autista da solo.`,
            };
        }
        if (l === 'en') {
            return {
                titolo: `Trip ${p.codice}: leaves ${quando} and ${motivo}`,
                corpo: `${rt}Less than ${p.minuti} minutes to departure. Reassign it or confirm it is covered: the system does not change driver by itself.`,
            };
        }
        return {
            titolo: `行程${p.codice}：${quando}出发，${motivo}`,
            corpo: `${rt}距出发不到${p.minuti}分钟。请重新指派或确认已有人负责：系统不会自行更换司机。`,
        };
    },
    // -------------------------------------------------------------- buste paga
    'bustaPaga.disponibile': (p, l) => {
        const etichetta = etichettaBusta(p, l);
        if (l === 'it') {
            return { titolo: `${etichetta} disponibile`, corpo: 'La trovi nella sezione Buste paga.' };
        }
        if (l === 'en') {
            return { titolo: `${etichetta} available`, corpo: 'You can find it in the Payslips section.' };
        }
        return { titolo: `${etichetta}已可查看`, corpo: '可在“工资单”中查看。' };
    },
    'bustaPaga.ritirata': (p, l) => {
        const etichetta = etichettaBusta(p, l);
        if (l === 'it') {
            return {
                titolo: `${etichetta} ritirata`,
                corpo: 'Il documento non è più disponibile. L’ufficio paghe ti fornirà quello corretto.',
            };
        }
        if (l === 'en') {
            return {
                titolo: `${etichetta} withdrawn`,
                corpo: 'The document is no longer available. Payroll will provide the corrected one.',
            };
        }
        return {
            titolo: `${etichetta}已撤回`,
            corpo: '该文件不再可用。薪资部门将提供更正后的版本。',
        };
    },
    // -------------------------------------------------------------- note spese
    'notaSpese.approvata': (p, l) => {
        const descr = notaTesto(nomeSpesaNellaLingua(p.tipo, p.codiceTipo, l), p.giorno, p.fuso, p.richiesto, l);
        // ⚠ Una spesa **non rimborsabile** si approva senza rimborso (K624): qui c'era solo il
        // ramo «piena o parziale», e con un rimborso a zero sarebbe uscito «approvata in parte —
        // rimborso 0,00 € invece di …, Motivo: null». Non e' un taglio, e' che non c'era niente da
        // rimborsare: lo si dice cosi'.
        if (p.rimborsabile === false) {
            if (l === 'it') {
                return {
                    titolo: 'Nota spese approvata',
                    corpo: `${descr}. È segnata come non rimborsabile, quindi non c’è nessun rimborso da ricevere.`,
                };
            }
            if (l === 'en') {
                return {
                    titolo: 'Expense note approved',
                    corpo: `${descr}. It is marked as not refundable, so there is no refund to receive.`,
                };
            }
            return {
                titolo: '报销单已批准',
                corpo: `${descr}。该费用被标记为不予报销，因此不会有报销款。`,
            };
        }
        const parziale = p.approvato < p.richiesto;
        const approvato = importoTesto(p.approvato, l);
        if (l === 'it') {
            return {
                titolo: parziale ? 'Nota spese approvata in parte' : 'Nota spese approvata',
                corpo: parziale
                    ? `${descr}. Rimborso approvato: ${approvato} invece di ${importoTesto(p.richiesto, l)}. Motivo: ${p.motivo}`
                    : `${descr}. Rimborso approvato: ${approvato}.`,
            };
        }
        if (l === 'en') {
            return {
                titolo: parziale ? 'Expense note partly approved' : 'Expense note approved',
                corpo: parziale
                    ? `${descr}. Refund approved: ${approvato} instead of ${importoTesto(p.richiesto, l)}. Reason: ${p.motivo}`
                    : `${descr}. Refund approved: ${approvato}.`,
            };
        }
        return {
            titolo: parziale ? '报销单已部分批准' : '报销单已批准',
            corpo: parziale
                ? `${descr}。已批准报销：${approvato}，而非${importoTesto(p.richiesto, l)}。原因：${p.motivo}`
                : `${descr}。已批准报销：${approvato}。`,
        };
    },
    'notaSpese.rifiutata': (p, l) => {
        const descr = notaTesto(nomeSpesaNellaLingua(p.tipo, p.codiceTipo, l), p.giorno, p.fuso, p.importo, l);
        // Il motivo — testo di chi decide — va in coda in tutte e tre le lingue: così il confine
        // fra la frase del sistema e le parole libere non lascia in cinese uno spazio dopo un
        // ideogramma, e chi legge ha già davanti che cosa fare quando arriva al perché.
        if (l === 'it') {
            return {
                titolo: 'Nota spese rifiutata',
                corpo: `${descr}. Puoi correggerla e ripresentarla: la nota resta dov’era. Motivo: ${p.motivo}`,
            };
        }
        if (l === 'en') {
            return {
                titolo: 'Expense note refused',
                corpo: `${descr}. You can correct it and resubmit: the note stays where it was. Reason: ${p.motivo}`,
            };
        }
        return {
            titolo: '报销单已驳回',
            corpo: `${descr}。你可以更正后重新提交，单据仍在原处。原因：${p.motivo}`,
        };
    },
    'notaSpese.rimborsata': (p, l) => {
        const descr = notaTesto(nomeSpesaNellaLingua(p.tipo, p.codiceTipo, l), p.giorno, p.fuso, p.importo, l);
        const soldi = importoTesto(p.importo, l);
        const cedolino = cedolinoDelRimborso(p.periodo, l);
        if (l === 'it') {
            return {
                titolo: 'Nota spese rimborsata',
                corpo: `${descr}. ${soldi} ${cedolino}.`,
            };
        }
        if (l === 'en') {
            return {
                titolo: 'Expense note reimbursed',
                corpo: `${descr}. ${soldi} ${cedolino}.`,
            };
        }
        return { titolo: '报销单已报销', corpo: `${descr}。${soldi}${cedolino}。` };
    },
    /*
      **L'importo, il tipo e il giorno** salgono nel sottotitolo: «€ 84,50 · Pranzo · 12 set» è
      tutto quello su cui una nota spese si guarda per decidere se aprirla adesso.
  
      Qui il sottotitolo **non** rende legittimo un tasto «Approva», e vale la pena dirlo: un
      importo dichiarato non è uno scontrino, e approvare un rimborso senza aver guardato
      l'allegato è esattamente il gesto che questa app non deve rendere comodo.
    */
    'notaSpese.daRivedere': (p, l) => {
        const descr = notaTesto(nomeSpesaNellaLingua(p.tipo, p.codiceTipo, l), p.giorno, p.fuso, p.importo, l);
        if (l === 'it') {
            return {
                titolo: `Nota spese da rivedere: ${p.dipendente}`,
                sottotitolo: descr,
                corpo: 'È in attesa della tua decisione.',
            };
        }
        if (l === 'en') {
            return {
                titolo: `Expense note to review: ${p.dipendente}`,
                sottotitolo: descr,
                corpo: 'It is awaiting your decision.',
            };
        }
        return {
            titolo: `待审核报销单：${p.dipendente}`,
            sottotitolo: descr,
            corpo: '等待你的决定。',
        };
    },
    // Scritto a mano in tre lingue, come tutte le notifiche di sistema: il traduttore
    // automatico su queste righe cancellava i nomi propri e gli importi (K205), e qui il nome
    // del dipendente e l importo sono l unica cosa che serve a chi legge.
    'notaSpese.sollecito': (p, l) => {
        const descr = notaTesto(nomeSpesaNellaLingua(p.tipo, p.codiceTipo, l), p.giorno, p.fuso, p.importo, l);
        // «da 1 giorni» e il difetto che si legge subito e fa sembrare tutto approssimativo.
        if (l === 'it') {
            const quanto = p.giorni <= 1 ? 'da ieri' : `da ${p.giorni} giorni`;
            return {
                titolo: `Nota spese in attesa ${quanto}: ${p.dipendente}`,
                corpo: `${descr}. Aspetta la tua decisione ${quanto}.`,
            };
        }
        if (l === 'en') {
            const quanto = p.giorni <= 1 ? 'since yesterday' : `for ${p.giorni} days`;
            return {
                titolo: `Expense note waiting ${quanto}: ${p.dipendente}`,
                corpo: `${descr}. It has been awaiting your decision ${quanto}.`,
            };
        }
        const quanto = p.giorni <= 1 ? '自昨天' : `已${p.giorni}天`;
        return { titolo: `报销单待处理（${quanto}）：${p.dipendente}`, corpo: `${descr}。${quanto}等待你的决定。` };
    },
    // ------------------------------------------------------------- spedizioni
    'spedizione.urgente': (p, l) => {
        const via = `${p.partenza} → ${p.arrivo}`;
        if (l === 'it') {
            const titolo = p.cosa === 'creata'
                ? `Spedizione urgente ${p.codice}`
                : p.cosa === 'annullata'
                    ? `Spedizione urgente annullata: ${p.codice}`
                    : p.urgente
                        ? `Spedizione urgente aggiornata: ${p.codice}`
                        : `Spedizione non più urgente: ${p.codice}`;
            return { titolo, corpo: `${via}. ${p.urgente ? 'Alta priorità.' : 'Priorità abbassata.'}` };
        }
        if (l === 'en') {
            const titolo = p.cosa === 'creata'
                ? `Urgent shipment ${p.codice}`
                : p.cosa === 'annullata'
                    ? `Urgent shipment cancelled: ${p.codice}`
                    : p.urgente
                        ? `Urgent shipment updated: ${p.codice}`
                        : `Shipment no longer urgent: ${p.codice}`;
            return { titolo, corpo: `${via}. ${p.urgente ? 'High priority.' : 'Priority lowered.'}` };
        }
        const titolo = p.cosa === 'creata'
            ? `紧急运单${p.codice}`
            : p.cosa === 'annullata'
                ? `紧急运单已取消：${p.codice}`
                : p.urgente
                    ? `紧急运单已更新：${p.codice}`
                    : `运单不再紧急：${p.codice}`;
        return { titolo, corpo: `${via}。${p.urgente ? '高优先级。' : '优先级已降低。'}` };
    },
    'logistica.praticheScadute': (p, l) => {
        const uno = p.quante === 1;
        // Il fuso manca solo nelle notifiche composte prima del 2026-10-19 (K664), che non
        // portavano nemmeno le date: lì non serve a niente, e qui non si inventa.
        const fuso = p.fuso ?? 'Europe/Rome';
        const righe = [
            viaggiScaduti(p.viaggi, p.dettaglioViaggi, fuso, l),
            spedizioniScadute(p.spedizioni, p.dettaglioSpedizioni, fuso, l),
        ].filter((r) => r !== null);
        if (l === 'it') {
            return {
                titolo: `${p.quante} ${uno ? 'pratica scaduta' : 'pratiche scadute'} in logistica`,
                corpo: `${righe.join(' ')} ${uno
                    ? 'È uscita dalla pianificazione: se serve ancora va riaperta.'
                    : 'Sono uscite dalla pianificazione: se servono ancora vanno riaperte.'}`,
            };
        }
        if (l === 'en') {
            return {
                titolo: `${p.quante} logistics item${uno ? '' : 's'} expired`,
                corpo: `${righe.join(' ')} ${uno
                    ? 'It has left the planning board: reopen it if it is still needed.'
                    : 'They have left the planning board: reopen them if they are still needed.'}`,
            };
        }
        return {
            titolo: `${p.quante}项物流已过期`,
            corpo: `${righe.join('')}已移出计划：如仍需要，请重新打开。`,
        };
    },
    // -------------------------------------------------------------------- chat
    'chat.messaggio': (p, l) => {
        // L'anteprima e il mittente sono dati: le tre lingue coincidono, tranne i due punti fra
        // nome e testo nella chat di stanza, che in cinese sono ： e non vogliono lo spazio.
        if (p.direct) {
            return { titolo: p.mittente, corpo: p.anteprima };
        }
        const stanza = p.stanza ?? (l === 'it' ? 'Conversazione' : l === 'en' ? 'Conversation' : '对话');
        return {
            titolo: stanza,
            corpo: `${p.mittente}${l === 'zh' ? '：' : ': '}${p.anteprima}`,
        };
    },
    'chat.segnalazione': (p, l) => {
        // I motivi scritti per esteso nelle tre lingue e non composti da una chiave: chi li
        // legge è l'ufficio del personale, e «off_topic» in una notifica non è una parola.
        const motivo = l === 'it'
            ? { OFFENSIVE: 'contenuto offensivo', CONFIDENTIAL: 'dati riservati', OFF_TOPIC: 'non c’entra col lavoro', OTHER: 'altro' }[p.motivo]
            : l === 'en'
                ? { OFFENSIVE: 'offensive content', CONFIDENTIAL: 'confidential data', OFF_TOPIC: 'not work-related', OTHER: 'other' }[p.motivo]
                : { OFFENSIVE: '冒犯性内容', CONFIDENTIAL: '机密信息', OFF_TOPIC: '与工作无关', OTHER: '其他' }[p.motivo];
        if (l === 'it') {
            return {
                titolo: 'Un messaggio segnalato',
                corpo: `${p.chi} ha segnalato un messaggio di ${p.contro}: ${motivo}.`,
            };
        }
        if (l === 'en') {
            return {
                titolo: 'A reported message',
                corpo: `${p.chi} reported a message from ${p.contro}: ${motivo}.`,
            };
        }
        return { titolo: '有消息被举报', corpo: `${p.chi}举报了${p.contro}的一条消息：${motivo}。` };
    },
    // ---------------------------------------------------------------- riunioni
    'riunione.risposta': (p, l) => {
        const quando = quandoTesto(p.inizio, p.fuso, l);
        if (l === 'it') {
            const parola = p.stato === 'accettato'
                ? 'ha accettato'
                : p.stato === 'rifiutato'
                    ? 'ha rifiutato'
                    : 'ha risposto «forse»';
            return {
                titolo: `${p.chi} ${parola}: ${p.titolo}`,
                corpo: p.esterno ? `${quando} · invitato esterno, ha risposto dalla mail` : quando,
            };
        }
        if (l === 'en') {
            const parola = p.stato === 'accettato'
                ? 'accepted'
                : p.stato === 'rifiutato'
                    ? 'declined'
                    : "replied 'maybe'";
            return {
                titolo: `${p.chi} ${parola}: ${p.titolo}`,
                corpo: p.esterno ? `${quando} · external guest, replied by email` : quando,
            };
        }
        const parola = p.stato === 'accettato' ? '已接受' : p.stato === 'rifiutato' ? '已拒绝' : '回复“待定”';
        return {
            titolo: `${p.chi}${parola}：${p.titolo}`,
            corpo: p.esterno ? `${quando} · 外部嘉宾，通过邮件回复` : quando,
        };
    },
    // ---------------------------------------------------------------- proposte
    'proposta.accettata': (p, l) => {
        const per = finestra(p.dal, p.al, p.fuso, l);
        if (l === 'it') {
            return { titolo: `Proposta accettata: ${p.dipendente}`, corpo: `${nomeAssenzaNellaLingua(p.tipo, p.codiceTipo, l)} ${per}. Ora è approvata.` };
        }
        if (l === 'en') {
            return { titolo: `Proposal accepted: ${p.dipendente}`, corpo: `${nomeAssenzaNellaLingua(p.tipo, p.codiceTipo, l)} ${per}. It is now approved.` };
        }
        return { titolo: `提议已接受：${p.dipendente}`, corpo: `${nomeAssenzaNellaLingua(p.tipo, p.codiceTipo, l)}${per}。现已批准。` };
    },
    'proposta.rifiutata': (p, l) => {
        const base = `${nomeAssenzaNellaLingua(p.tipo, p.codiceTipo, l)} ${finestra(p.dal, p.al, p.fuso, l)}`;
        if (l === 'it') {
            return {
                titolo: `Proposta rifiutata: ${p.dipendente}`,
                corpo: p.motivo ? `${base}. Motivo: ${p.motivo}` : base,
            };
        }
        if (l === 'en') {
            return {
                titolo: `Proposal refused: ${p.dipendente}`,
                corpo: p.motivo ? `${base}. Reason: ${p.motivo}` : base,
            };
        }
        return {
            titolo: `提议已拒绝：${p.dipendente}`,
            corpo: p.motivo ? `${base}。原因：${p.motivo}` : base,
        };
    },
    'proposta.ritirata': (p, l) => {
        const per = finestra(p.dal, p.al, p.fuso, l);
        if (l === 'it') {
            return { titolo: 'Proposta di ferie ritirata', corpo: `${nomeAssenzaNellaLingua(p.tipo, p.codiceTipo, l)} ${per}. Ritirata da ${p.chi}.` };
        }
        if (l === 'en') {
            return { titolo: 'Leave proposal withdrawn', corpo: `${nomeAssenzaNellaLingua(p.tipo, p.codiceTipo, l)} ${per}. Withdrawn by ${p.chi}.` };
        }
        return { titolo: '休假提议已撤回', corpo: `${nomeAssenzaNellaLingua(p.tipo, p.codiceTipo, l)}${per}。由${p.chi}撤回。` };
    },
    /*
      I tre della mensa.
  
      L'ora di chiusura si scrive con `oraTesto`, cioè con `Intl` e il fuso aziendale, e non
      come «09:40» ricopiato: quell'ora è una manopola, e una frase che la ricopia diventa
      falsa il giorno che qualcuno la muove — senza che nessun cancello se ne accorga, perché
      una stringa resta una stringa valida.
  
      Il giorno **non** compare nel corpo del promemoria delle 08:30 e del richiamo delle 09:30:
      tutti e due parlano di oggi, e scrivere «oggi, lunedì 14 settembre» in una notifica che
      arriva stamattina è rumore. Il giorno è nei parametri lo stesso, perché il modello deve
      poter essere reso anche fuori dal suo orario (una prova, un rinvio) senza mentire.
    */
    'mensa.promemoria': (p, l) => {
        const ora = oraTesto(p.chiude, p.fuso, l);
        if (l === 'it') {
            return {
                titolo: 'Oggi c’è la mensa',
                corpo: `${p.piatti === 1 ? 'Un piatto' : `${p.piatti} piatti`} in menù. Si ordina fino alle ${ora}.`,
            };
        }
        if (l === 'en') {
            return {
                titolo: 'Canteen is on today',
                corpo: `${p.piatti === 1 ? 'One dish' : `${p.piatti} dishes`} on the menu. Ordering closes at ${ora}.`,
            };
        }
        return { titolo: '今天有食堂', corpo: `菜单上有${p.piatti}道菜。${ora}前可以订餐。` };
    },
    'mensa.daConfermare': (p, l) => {
        const ora = oraTesto(p.chiude, p.fuso, l);
        if (l === 'it') {
            return {
                titolo: 'Il tuo ordine non è confermato',
                corpo: `Hai scelto dei piatti e non hai confermato. Alle ${ora} l’ordine si chiude e quello che non è confermato non parte.`,
            };
        }
        if (l === 'en') {
            return {
                titolo: 'Your order is not confirmed',
                corpo: `You picked some dishes but did not confirm. Ordering closes at ${ora}, and what is not confirmed does not go through.`,
            };
        }
        return {
            titolo: '您的订餐尚未确认',
            corpo: `您选了菜但没有确认。${ora}订餐关闭，未确认的不会提交。`,
        };
    },
    'mensa.sedeSpenta': (p, l) => {
        const uno = p.quanti === 1;
        if (l === 'it') {
            return {
                titolo: 'La mensa è stata spenta',
                corpo: `In ${p.sede} il servizio non è più attivo. ` +
                    `${uno ? 'Il tuo ordine' : `I tuoi ${p.quanti} ordini`} dei prossimi giorni ` +
                    `${uno ? 'è stato annullato' : 'sono stati annullati'}.`,
            };
        }
        if (l === 'en') {
            return {
                titolo: 'The canteen has been switched off',
                corpo: `The service is no longer active at ${p.sede}. ` +
                    `${uno ? 'Your order' : `Your ${p.quanti} orders`} for the coming days ` +
                    `${uno ? 'has' : 'have'} been cancelled.`,
            };
        }
        return {
            titolo: '食堂已停用',
            corpo: `${p.sede}的服务已停用。您接下来几天的${p.quanti}份订餐已取消。`,
        };
    },
    'mensa.piattoTolto': (p, l) => {
        const g = giornoTesto(p.giorno, p.fuso, l);
        const uno = p.piatti.length === 1;
        /*
          L'elenco si compone qui e non nel chiamante: in cinese la virgola che separa gli elementi
          di una lista non è quella della frase (、 contro ，), e una lista costruita altrove
          arriverebbe qui già punteggiata all'italiana in tutte e tre le lingue.
        */
        const elenco = p.piatti.join(l === 'zh' ? '、' : ', ');
        const vuoto = p.restano === 0;
        if (l === 'it') {
            return {
                titolo: vuoto
                    ? 'Il tuo ordine è stato annullato'
                    : uno
                        ? 'Un piatto non c’è più'
                        : 'Alcuni piatti non ci sono più',
                corpo: `Il menù di ${g} è stato corretto: ${elenco} ${uno ? 'non è' : 'non sono'} più in ` +
                    (vuoto
                        ? 'menù, e non avevi scelto altro: l’ordine è stato annullato. Se fai in tempo, ordina di nuovo.'
                        : 'menù. Il resto del tuo ordine è rimasto.'),
            };
        }
        if (l === 'en') {
            return {
                titolo: vuoto
                    ? 'Your order was cancelled'
                    : uno
                        ? 'A dish is gone'
                        : 'Some dishes are gone',
                corpo: `The menu for ${g} was corrected: ${elenco} ${uno ? 'is' : 'are'} no longer on it` +
                    (vuoto
                        ? ', and you had picked nothing else: the order was cancelled. Order again if there is still time.'
                        : '. The rest of your order is unchanged.'),
            };
        }
        return {
            titolo: vuoto ? '您的订餐已取消' : uno ? '有一道菜已下架' : '有几道菜已下架',
            corpo: vuoto
                ? `${g}的菜单已更正：${elenco}不再供应，而您没有选其他菜，订餐已取消。如果还来得及，请重新订餐。`
                : `${g}的菜单已更正：${elenco}不再供应。您订餐的其余部分保持不变。`,
        };
    },
    'mensa.modificheNonConfermate': (p, l) => {
        const ora = oraTesto(p.chiude, p.fuso, l);
        if (l === 'it') {
            return {
                titolo: 'Le tue modifiche non sono confermate',
                corpo: `Stai cambiando un ordine già confermato. Se non confermi entro le ${ora}, vale l’ordine di prima.`,
            };
        }
        if (l === 'en') {
            return {
                titolo: 'Your changes are not confirmed',
                corpo: `You are changing an order that is already confirmed. If you do not confirm by ${ora}, the previous order stands.`,
            };
        }
        return {
            titolo: '您的修改尚未确认',
            corpo: `您正在修改已确认的订餐。若未在${ora}前确认，将以之前的订餐为准。`,
        };
    },
    // ------------------------------------------------- le ultime cinque, portate il 2026-09-15
    'richiesta.annullata': (p, l) => {
        const per = periodo(p.dal, p.al, p.giorni, p.fuso, l, p.ore);
        if (l === 'it') {
            return {
                titolo: `Annullata: ${assenza(p.tipo, l, p.codiceTipo)}`,
                corpo: `${per}. L’ha annullata ${p.chi}.` + (p.motivo ? ` Motivo: ${p.motivo}` : ''),
            };
        }
        if (l === 'en') {
            return {
                titolo: `Cancelled: ${assenza(p.tipo, l, p.codiceTipo)}`,
                corpo: `${per}. Cancelled by ${p.chi}.` + (p.motivo ? ` Reason: ${p.motivo}` : ''),
            };
        }
        return {
            titolo: `已取消：${assenza(p.tipo, l, p.codiceTipo)}`,
            corpo: `${per}。由${p.chi}取消。` + (p.motivo ? `原因：${p.motivo}` : ''),
        };
    },
    'viaggio.tolto': (p, l) => {
        if (l === 'it') {
            return {
                titolo: `Il viaggio ${p.codice} non è più tuo`,
                corpo: 'È stato riassegnato a un altro autista.',
            };
        }
        if (l === 'en') {
            return {
                titolo: `Trip ${p.codice} is no longer yours`,
                corpo: 'It has been reassigned to another driver.',
            };
        }
        return { titolo: `行程${p.codice}已不再由您负责`, corpo: '已改派给其他司机。' };
    },
    // tregenereAvvisiAnnullo (§78 seconda parte, 2026-11-01): i tre annulli che prima tacevano.
    'viaggio.annullato': (p, l) => {
        const quando = quandoTesto(p.quando, p.fuso, l);
        if (l === 'it') {
            return {
                titolo: `Viaggio ${p.codice} annullato`,
                corpo: `Era in partenza ${quando}. Annullato da ${p.chi}.` + (p.motivo ? ` Motivo: ${p.motivo}` : ''),
            };
        }
        if (l === 'en') {
            return {
                titolo: `Trip ${p.codice} cancelled`,
                corpo: `It was due to leave ${quando}. Cancelled by ${p.chi}.` + (p.motivo ? ` Reason: ${p.motivo}` : ''),
            };
        }
        return {
            titolo: `行程${p.codice}已取消`,
            corpo: `原定${quando}出发。由${p.chi}取消。` + (p.motivo ? `原因：${p.motivo}` : ''),
        };
    },
    'notaSpese.ritirata': (p, l) => {
        const descr = notaTesto(nomeSpesaNellaLingua(p.tipo, p.codiceTipo, l), p.giorno, p.fuso, p.importo, l);
        if (l === 'it') {
            return {
                titolo: 'Nota spese ritirata',
                corpo: `${descr}. Ritirata da ${p.chi}: è tornata in bozza, puoi correggerla e ripresentarla.`,
            };
        }
        if (l === 'en') {
            return {
                titolo: 'Expense note withdrawn',
                corpo: `${descr}. Withdrawn by ${p.chi}: it is back in draft, you can correct it and resubmit.`,
            };
        }
        return {
            titolo: '报销单已撤回',
            corpo: `${descr}。由${p.chi}撤回：已退回草稿，你可以更正后重新提交。`,
        };
    },
    'divise.annullata': (p, l) => {
        const capi = elencoCapi(p.capi, l);
        if (l === 'it') {
            return {
                titolo: 'Richiesta vestiario annullata',
                corpo: `${capi}. Annullata da ${p.chi}.` + (p.motivo ? ` Motivo: ${p.motivo}` : ''),
            };
        }
        if (l === 'en') {
            return {
                titolo: 'Uniform request cancelled',
                corpo: `${capi}. Cancelled by ${p.chi}.` + (p.motivo ? ` Reason: ${p.motivo}` : ''),
            };
        }
        return {
            titolo: '工装申请已取消',
            corpo: `${capi}。由${p.chi}取消。` + (p.motivo ? `原因：${p.motivo}` : ''),
        };
    },
    'viaggio.tappaAggiunta': (p, l) => {
        if (l === 'it') {
            return {
                titolo: `Tappa aggiunta al viaggio ${p.codice}`,
                corpo: `L’ufficio ha aggiunto una tappa: ${p.luogo}. Apri il viaggio per vederla nel percorso.`,
            };
        }
        if (l === 'en') {
            return {
                titolo: `Stop added to trip ${p.codice}`,
                corpo: `The office added a stop: ${p.luogo}. Open the trip to see it in the route.`,
            };
        }
        return {
            titolo: `行程${p.codice}新增停靠点`,
            corpo: `办公室新增了一个停靠点：${p.luogo}。请打开行程在路线中查看。`,
        };
    },
    /*
      Il carico cambiato a viaggio partito.
  
      È il gemello di `viaggio.tappaAggiunta` e serve al caso in cui quello **non scatta**: se la
      spedizione si attacca a tappe che il percorso ha già, nessuna tappa nasce, quindi nessun
      avviso parte — e l'autista arriva a una fermata che conosceva e si trova un collo in più che
      nessuno gli ha detto. Misurato il 2026-10-12: `linkTransfer` non avvisava nessuno.
  
      Il testo dice **dove**, non solo che cosa: a un autista in strada «è cambiato il carico» non
      serve: gli serve sapere a quale fermata deve caricare qualcosa di nuovo. Il codice della
      spedizione c'è perché è quello che legge sul collo.
  
      ⚠ Il nome del luogo e il codice non passano dal traduttore — sono voci d'anagrafica, e il
      traduttore cancella i nomi propri: per questo le tre lingue stanno scritte qui a mano, come
      per tutte le altre notifiche di sistema.
    */
    'viaggio.spedizioneAggiunta': (p, l) => {
        if (l === 'it') {
            return {
                titolo: `Carico aggiunto al viaggio ${p.codice}`,
                corpo: `L’ufficio ha aggiunto la spedizione ${p.spedizione} da ritirare a ${p.luogo}. Apri il viaggio per vedere il percorso aggiornato.`,
            };
        }
        if (l === 'en') {
            return {
                titolo: `Load added to trip ${p.codice}`,
                corpo: `The office added shipment ${p.spedizione}, to be picked up at ${p.luogo}. Open the trip to see the updated route.`,
            };
        }
        return {
            titolo: `行程${p.codice}新增装载`,
            corpo: `办公室新增了运单${p.spedizione}，需在${p.luogo}提货。请打开行程查看更新后的路线。`,
        };
    },
    /*
      L'italiano è copiato dal sistema vecchio e non si tocca: il perché sta sul tipo, sopra.
      Le altre due lingue nascono qui, e dicono la stessa cosa senza la parola vuota in testa —
      «Account closure notice» dice già che cos'è, mentre «Avviso» da solo non lo direbbe.
    */
    'app.nuovaVersione': (p, l) => {
        const store = p.piattaforma === 'ios' ? 'App Store' : 'Google Play';
        if (l === 'it') {
            return {
                titolo: `BesteHR ${p.versione} è disponibile`,
                corpo: `Aggiorna l’app da ${store}: con la versione nuova ricevi le notifiche e trovi ` +
                    'le ultime correzioni.',
            };
        }
        if (l === 'en') {
            return {
                titolo: `BesteHR ${p.versione} is available`,
                corpo: `Update the app from ${store} to receive notifications and get the latest fixes.`,
            };
        }
        return {
            titolo: `BesteHR ${p.versione}已发布`,
            corpo: `请在${store}更新应用，即可接收通知并获得最新修复。`,
        };
    },
    'utenza.dismissione': (p, l) => {
        if (l === 'it') {
            return {
                titolo: 'Avviso dismissione utenza',
                corpo: `Hai a disposizione ${p.giorni} giorni per scaricare la documentazione associata ` +
                    'alla tua utenza prima che venga dismessa.',
            };
        }
        if (l === 'en') {
            return {
                titolo: 'Your account will be closed',
                corpo: `You have ${p.giorni} days to download the documents linked to your account before ` +
                    'it is closed.',
            };
        }
        return {
            titolo: '您的账号即将注销',
            corpo: `您有${p.giorni}天时间下载与您账号关联的文件，之后账号将被注销。`,
        };
    },
    // ------------------------------------------------- le automazioni che smettono di funzionare
    'automazioni.guaste': (p, l) => {
        const una = p.nuove.length === 1;
        const elenco = p.nuove.map((g) => guasto(g, l)).join(l === 'zh' ? '；' : '; ');
        /*
          «e altre N» solo quando ce ne sono di già dette. Il totale da solo, senza questa coda,
          sarebbe un numero che non torna con l'elenco sotto — e chi lo legge si mette a contare
          invece di aprire il Cruscotto.
        */
        const altre = p.totale - p.nuove.length;
        if (l === 'it') {
            return {
                titolo: una ? 'Un’automazione non funziona' : `${p.nuove.length} automazioni non funzionano`,
                corpo: `${elenco}.` +
                    (altre > 0 ? (altre === 1 ? ' Più una già segnalata.' : ` Più ${altre} già segnalate.`) : '') +
                    ' Aprile in Automazioni, nel Cruscotto: lì c’è l’errore per esteso.',
            };
        }
        if (l === 'en') {
            return {
                titolo: una ? 'An automation is not working' : `${p.nuove.length} automations are not working`,
                corpo: `${elenco}.` +
                    (altre > 0 ? ` Plus ${altre} already reported.` : '') +
                    ' Open Automations in the dashboard: the full error is there.',
            };
        }
        return {
            titolo: una ? '有一项自动任务异常' : `有${p.nuove.length}项自动任务异常`,
            corpo: `${elenco}。` +
                (altre > 0 ? `另有${altre}项已报告。` : '') +
                '请在控制台的“自动任务”中查看完整错误。',
        };
    },
    'automazioni.riprese': (p, l) => {
        const una = p.tornate.length === 1;
        const elenco = p.tornate.join(l === 'zh' ? '、' : ', ');
        if (l === 'it') {
            return {
                titolo: una ? 'L’automazione è tornata a posto' : 'Le automazioni sono tornate a posto',
                corpo: `${elenco}: ${una ? 'gira' : 'girano'} di nuovo senza errori. Non resta niente da guardare.`,
            };
        }
        if (l === 'en') {
            return {
                titolo: una ? 'The automation is working again' : 'The automations are working again',
                corpo: `${elenco}: running again without errors. Nothing left to look at.`,
            };
        }
        return {
            titolo: una ? '自动任务已恢复正常' : '自动任务已全部恢复正常',
            corpo: `${elenco}：已恢复正常运行，无需再处理。`,
        };
    },
    'servizi.rialzati': (p, l) => {
        const uno = p.servizi.length === 1;
        const elenco = p.servizi
            .map((s) => {
            const ora = oraTesto(s.da, p.fuso, l);
            if (l === 'it') {
                return s.quante === 1 ? `${s.nome}, alle ${ora}` : `${s.nome}, ${s.quante} volte dalle ${ora}`;
            }
            if (l === 'en') {
                return s.quante === 1 ? `${s.nome}, at ${ora}` : `${s.nome}, ${s.quante} times since ${ora}`;
            }
            return s.quante === 1 ? `${s.nome}，${ora}` : `${s.nome}，自${ora}起${s.quante}次`;
        })
            .join(l === 'zh' ? '；' : '; ');
        if (l === 'it') {
            return {
                titolo: uno
                    ? 'La macchina ha rialzato un servizio da sola'
                    : `La macchina ha rialzato ${p.servizi.length} servizi da sola`,
                /*
                  Il participio si scrive per intero nei due rami invece di attaccarlo fuori dal
                  ternario: «l'ha riavviati» — che è quello che usciva — non è un refuso che si nota
                  rileggendo il codice, si nota **leggendo la notifica**, e questa qui la legge un
                  amministratore che deve fidarsi di quel che c'è scritto.
                */
                /*
                  ⚠ Dal 2026-10-18 la traccia la scrivono in due ([[K653]]): la sorveglianza, quando un
                  servizio acceso non risponde e lo riavvia, e il conto dei riavvii, quando un servizio
                  cade e systemd lo fa ripartire troppe volte in un quarto d'ora. Prima qui c'era «si era
                  piantato e la sorveglianza l'ha riavviato», che per il secondo caso sarebbe falso: la
                  frase adesso dice quel che vale per tutt'e due.
                */
                corpo: `${elenco}. ` +
                    (uno
                        ? "Si era fermato e la macchina l'ha rimesso in piedi da sola: adesso funziona"
                        : 'Si erano fermati e la macchina li ha rimessi in piedi da sola: adesso funzionano') +
                    ', ma resta da capire perché.',
            };
        }
        if (l === 'en') {
            return {
                titolo: uno
                    ? 'The machine restarted a service by itself'
                    : `The machine restarted ${p.servizi.length} services by itself`,
                corpo: `${elenco}. ${uno ? 'It stopped' : 'They stopped'} and the machine brought ` +
                    `${uno ? 'it' : 'them'} back by itself: it works now, but why it happened is still open.`,
            };
        }
        return {
            titolo: uno ? '系统已自动重启一项服务' : `系统已自动重启${p.servizi.length}项服务`,
            corpo: `${elenco}。服务曾停止，系统已自动将其恢复：现已正常，但原因仍需查明。`,
        };
    },
};
/**
 * Il testo di una notifica di sistema, in una lingua.
 *
 * Non fallisce mai su una chiave sconosciuta perché non può esistere: `ModelloNotifica` è
 * l'unione delle chiavi dichiarate, e una chiave nuova senza il suo modello non compila —
 * `Reso` pretende un renderer per **ognuna** delle chiavi di `ParametriNotifica`. È lo
 * stesso vincolo con cui i dizionari della app tengono d'accordo le tre lingue, portato
 * dove le frasi le scrive il server.
 */
export function rendiNotifica(modello, lingua) {
    const reso = MODELLI[modello.chiave];
    return reso(modello.parametri, lingua);
}
/** Le tre rese in un colpo solo: è quello che serve a chi scrive la notifica e le sue righe. */
export function rendiNotificaInOgniLingua(modello) {
    const fuori = {};
    for (const lingua of LINGUE)
        fuori[lingua] = rendiNotifica(modello, lingua);
    return fuori;
}
//# sourceMappingURL=notifiche-modelli.js.map